import { CLUB_PRESETS } from '../constants';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { predictFlightFrame } from '../camera/predictFlightFrame';
import type { ShotSimulator } from '../simulation/ShotSimulator';
import { SimPhase } from '../types';
import type { Tempo, Transition, ShotResult, ClubPreset } from '../types';
import { createSwingParams, computeFaceToPath, deriveSpinAxisFromImpact, deriveLaunchDirection } from '../simulation/SwingInput';
import type { ShotTracer } from '../rendering/ShotTracer';
import type { CameraSystem } from '../camera/CameraSystem';
import { AudioManager } from '../audio/AudioManager';
import { computeSpecModifiers, applyModifiers } from '../equipment/EquipmentEngine';
import type { GearSpecs, ShaftFlex, IronCategory, EldrickPayload } from '../equipment/types';
import { getHandicapProfile, scalePreset } from '../equipment/PlayerProfile';
import { TestPhase, buildSpecDetail } from '../testing/GearTestOrchestrator';
import type { GearTestOrchestrator, ComparisonEntry } from '../testing/GearTestOrchestrator';
import { FavoritesManager } from './FavoritesManager';
import { generateFittingRecommendation } from '../testing/FittingRecommendation';
import { SessionStore } from '../persistence/SessionStore';
import type { FittingSession } from '../persistence/SessionStore';
import { renderShotShape } from '../rendering/ShotShapePreview';
import { MOCK_ELDRICK_PAYLOAD } from '../testing/eldrickMock';
import { FittingArrival } from './FittingArrival';
import { importedLaunch } from '../testing/ImportedFitting';
import { buildFittingSummary } from '../testing/FittingSummary';
import { fittingTimeScale, isComparisonPhase } from './FittingPace';
import { bestFittingEntry, fittingScore, similarPerformance } from './FittingScore';
import { parseFittingReference } from './FittingReference';
// Ball library now accessed via orchestrator — no direct import needed

// ── Google Analytics helper ─────────────────────────────────────────────────
declare global { interface Window { gtag?: (...args: unknown[]) => void; } }

function trackEvent(name: string, params?: Record<string, string | number | boolean>): void {
  try { window.gtag?.('event', name, params); } catch { /* noop */ }
}


/**
 * Generate a contextual "why" explanation comparing a config to baseline.
 * Returns 1-2 concise reasons why this config performs better or worse.
 */
function generateWhyExplanation(entry: ComparisonEntry, baseline: ComparisonEntry): string {
  const reasons: string[] = [];
  const carryDelta = (entry.pattern?.typicalCarry ?? Number(entry.formatted.carry))
    - (baseline.pattern?.typicalCarry ?? Number(baseline.formatted.carry));
  reasons.push(`<strong>${carryDelta >= 0 ? '+' : ''}${carryDelta.toFixed(1)}</strong> yd typical carry · simulated`);
  if (entry.pattern && baseline.pattern) {
    const width = entry.pattern.landingWidth - baseline.pattern.landingWidth;
    const miss = entry.pattern.badMiss - baseline.pattern.badMiss;
    reasons.push(`${Math.abs(width).toFixed(1)} yd ${width <= 0 ? 'narrower' : 'wider'} landing pattern`);
    reasons.push(`${Math.abs(miss).toFixed(1)} yd ${miss <= 0 ? 'smaller' : 'larger'} 90th-percentile lateral miss`);
  }
  const spinDelta = entry.spinRate - baseline.spinRate;
  if (Math.abs(spinDelta) > 200) {
    reasons.push(`${spinDelta >= 0 ? '+' : ''}${Math.round(spinDelta)} rpm launch spin; not inherently better or worse`);
  }
  return reasons.join('<br>');
}

/** Generate a short one-line "why" for inline display in results tables. */
function generateShortWhy(entry: ComparisonEntry, baseline: ComparisonEntry): string {
  const carryDelta = (parseFloat(entry.formatted.carry) || 0) - (parseFloat(baseline.formatted.carry) || 0);
  if (entry.pattern && baseline.pattern) {
    const width = entry.pattern.landingWidth - baseline.pattern.landingWidth;
    return `${carryDelta >= 0 ? '+' : ''}${carryDelta.toFixed(1)} yd carry · ${Math.abs(width).toFixed(1)} yd ${width <= 0 ? 'narrower' : 'wider'}`;
  }
  return `${carryDelta >= 0 ? '+' : ''}${carryDelta.toFixed(1)} yd carry · simulated`;
}

/** Default gear spec values — used for delta comparison and summary. */
const DEFAULT_SPECS: GearSpecs = {
  loft: 10.5,
  shaftWeight: 65,
  shaftFlex: 'S',
  ballCompression: 90,
  ballCover: 'urethane',
  ironCategory: 'players',
  clubLength: 45.5,
  swingWeight: 'D3',
  launchProfile: 'mid',
  headType: 'standard',
  weightSetting: 'standard',
};

/**
 * Manages all HTML overlay UI elements.
 * Wires up event listeners and updates displays.
 */
export class UIManager {
  private simulator: ShotSimulator;
  private camera: CameraSystem;
  private audio = new AudioManager();
  private orchestrator: GearTestOrchestrator;

  // DOM elements
  private hitBtn: HTMLButtonElement;
  private randomBtn: HTMLButtonElement;
  private clubSelect: HTMLSelectElement;
  private loadingEl: HTMLElement;

  // Current selected angle for shots
  private currentAngle: 'behind' | 'side' | 'top' = 'behind';

  // User baseline per club — generated from handicap + jitter
  private userBaseline: Record<string, ClubPreset> = {};


  // Selected spec entry — passed to startGearTest when user clicks TEST GEAR
  private selectedSpecEntry: ComparisonEntry | null = null;

  // Selected gear entry — passed to transitionToSummary
  private selectedGearEntry: ComparisonEntry | null = null;

  // Selected ball entry from ball testing step
  private selectedBallEntry: ComparisonEntry | null = null;
  private autoSelectingRecommendation = false;

  // Displayed totals — exact numbers the user sees at each step.
  // Used for all delta calculations so deltas always match the displayed values.
  private displayedBaselineTotal = 0;
  private displayedSpecTotal = 0;
  private displayedGearTotal = 0;
  private displayedBallTotal = 0;

  // Landing positions from last completed phase — for smart camera views

  // Current handicap for adaptive scoring
  private currentHandicap = 15;

  // Favorites
  private favoritesManager = new FavoritesManager();

  // Live leaderboard state
  private specLeaderboard: { entry: ComparisonEntry; shotIndex: number }[] = [];
  private gearLeaderboard: { entry: ComparisonEntry; shotIndex: number }[] = [];
  private ballLeaderboard: { entry: ComparisonEntry; shotIndex: number }[] = [];

  // ── Mobile ──
  private mobileRoot!: HTMLElement;
  private mobileSheet!: HTMLElement;
  private mobileCta!: HTMLButtonElement;
  private mobileSheetState: 'full' | 'half' | 'flight' | 'peek' | 'mini' | 'hidden' = 'full';
  private mobileCurrentContent: string = 'club';
  private heroTimer: ReturnType<typeof setTimeout> | null = null;
  private lastBaselineFormatted: Record<string, string> | null = null;
  private mobileSetupStep: 'club' | 'swing' = 'club';
  private specConfigsCompleted = 0;
  private gearConfigsCompleted = 0;
  private ballConfigsCompleted = 0;
  private fastResults = true;

  constructor(
    simulator: ShotSimulator,
    _tracer: ShotTracer,
    camera: CameraSystem,
    orchestrator: GearTestOrchestrator,
    private setPlaybackSpeed: (scale: number) => void = () => {},
  ) {
    this.simulator = simulator;
    this.camera = camera;
    this.orchestrator = orchestrator;

    this.hitBtn = document.getElementById('hit-btn') as HTMLButtonElement;
    this.randomBtn = document.getElementById('random-btn') as HTMLButtonElement;
    this.clubSelect = document.getElementById('club-select') as HTMLSelectElement;
    this.loadingEl = document.getElementById('loading')!;

    // ── Orchestrator callbacks ──
    this.orchestrator.onPhaseChange = (phase) => this.onTestPhaseChangeMobile(phase);
    this.orchestrator.onAllComplete = (results) => this.finalizeMobileSpecsCards(results);
    this.orchestrator.onSingleShotResult = (i, entry) => {
      this.updateMobileSpecCard(i, entry);
      this.specConfigsCompleted = (this.specConfigsCompleted || 0) + 1;
      this.updateProgressPill(this.specConfigsCompleted, this.orchestrator.getSpecCount());
    };
    this.orchestrator.onGearShotResult = (i, entry) => {
      this.updateMobileGearCard(i, entry);
      this.gearConfigsCompleted = (this.gearConfigsCompleted || 0) + 1;
      this.updateProgressPill(this.gearConfigsCompleted, 10);
    };
    this.orchestrator.onAllGearComplete = (results) => this.finalizeMobileGearCards(results);
    this.orchestrator.onBallShotResult = (i, entry) => {
      this.updateMobileBallCard(i, entry);
      this.ballConfigsCompleted = (this.ballConfigsCompleted || 0) + 1;
      this.updateProgressPill(this.ballConfigsCompleted, this.orchestrator.getBallConfigMeta().length);
    };
    this.orchestrator.onAllBallComplete = (results) => this.finalizeMobileBallCards(results);
    this.orchestrator.onShotsLanded = () => {
      this.camera.setFlightPoints(this.orchestrator.getFlightFramingPoints());
    };
    // Wire camera's dynamic position provider to orchestrator's active ball positions
    this.camera.getBallPositions = () => this.orchestrator.getActiveBallPositions();
    this.orchestrator.onFlightFramingChanged = points => this.camera.setFlightPoints(points);
    this.camera.getFrameWindow = () => {
      const canvas = document.getElementById('renderCanvas')!.getBoundingClientRect();
      const sheet = this.mobileSheet.getBoundingClientRect();
      const desktop = canvas.width >= 900;
      const right = desktop ? Math.min(canvas.right - 24, sheet.left - 24) : canvas.right - 22;
      const bottom = desktop ? canvas.bottom - 36 : Math.min(canvas.bottom - 100, sheet.top - 24);
      return {
        left: -1 + 44 / canvas.width,
        right: -1 + 2 * (right - canvas.left) / canvas.width,
        top: 1 - 2 * (document.getElementById('m-pace-control')!.hidden ? 142 : 202) / canvas.height,
        bottom: 1 - 2 * Math.max(220, bottom - canvas.top) / canvas.height,
      };
    };
    let frameResize: ReturnType<typeof setTimeout>;
    const layoutObserver = new ResizeObserver(() => {
      clearTimeout(frameResize);
      frameResize = setTimeout(() => this.camera.refreshFlightLayout(), 180);
    });
    layoutObserver.observe(document.getElementById('renderCanvas')!);
    layoutObserver.observe(document.getElementById('mobile-sheet')!);
    this.orchestrator.onAllBaselineComplete = (avgFormatted) => {
      this.lastBaselineFormatted = avgFormatted;
      // In ELDRICK flow, update the capture card with resolved distance data
      if (this.orchestrator.isEldrickFlow) {
        this.displayedBaselineTotal = parseFloat(avgFormatted.total || '0');
        document.getElementById('m-compiled-carry')!.textContent = avgFormatted.carry || '--';
        document.getElementById('m-compiled-total')!.textContent = avgFormatted.total || '--';
        document.getElementById('m-compiled-height')!.textContent = avgFormatted.height || '--';
        document.getElementById('m-compiled-land')!.textContent = avgFormatted.landing ? avgFormatted.landing + '\u00b0' : '--';
        document.getElementById('m-compiled-off')!.textContent = avgFormatted.offline || '--';
        if (this.orchestrator.phase === TestPhase.BaselineDone) {
          this.setSheetState('half');
          this.setMobileCta('COMPARE MY SPECS', false, true);
        }
      }
    };
    this.orchestrator.onBaselineShotProgress = (completed, total) => this.updateProgressPill(completed, total);
    this.orchestrator.onBaselineSingleShotResult = (i, formatted, spinRate) => {
      this.updateBaselineTableRow(i, formatted, spinRate);
    };
    this.orchestrator.onInFlightCarry = (data) => this.updateInFlightLeaderboard(data);

    this.bindEvents();
    this.bindFittingReferenceForm();

    // Generate initial baseline and populate fields
    const rawHcp = parseFloat((document.getElementById('handicap') as HTMLInputElement).value);
    const handicap = isNaN(rawHcp) ? 15 : rawHcp;
    this.currentHandicap = handicap;
    this.generateBaseline(handicap);
    this.onClubChange();

    // Default to side camera view
    this.camera.snapToView('side', false);

    // ── Mobile initialization ──
    this.mobileRoot = document.getElementById('mobile-root')!;
    this.mobileSheet = document.getElementById('mobile-sheet')!;
    this.mobileCta = document.getElementById('m-cta') as HTMLButtonElement;
    this.orchestrator.deferComparing = true;
    this.bindMobileEvents();
    this.bindMobileSyncInputs();
    this.bindSheetDrag();
    this.bindTapToInspect();
    this.onTestPhaseChangeMobile(TestPhase.Ready);

    // ── ELDRICK URL detection ──
    this.checkEldrickUrl();
  }

  // ── ELDRICK Integration ────────────────────────────────────────────────

  private bindFittingReferenceForm(): void {
    const form = document.getElementById('m-fitting-open-form') as HTMLFormElement | null;
    const input = document.getElementById('m-fitting-reference') as HTMLInputElement | null;
    const error = document.getElementById('m-fitting-open-error') as HTMLElement | null;
    if (!form || !input || !error) return;
    form.addEventListener('submit', event => {
      event.preventDefault();
      const path = parseFittingReference(input.value);
      if (!path) {
        error.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return;
      }
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      window.location.assign(path);
    });
  }

  private checkEldrickUrl(): void {
    const params = new URLSearchParams(window.location.search);

    // ?demo=eldrick → use mock payload
    if (params.get('demo') === 'eldrick') {
      trackEvent('session_start', { flow: 'eldrick_demo' });
      this.camera.animateToVec({ x: 46, y: 65, z: -80 }, { x: 0, y: 0, z: 150 }, 1.0, 1.4, 'freeLook');
      new FittingArrival(payload => this.startEldrickFlow(payload)).present(MOCK_ELDRICK_PAYLOAD, true);
      return;
    }

    // /<fittingId> path → fetch from backend (mirrors eldrick.com/results/<id>)
    const pathId = window.location.pathname.replace(/^\//, '').split('/')[0];
    if (pathId) {
      trackEvent('session_start', { flow: 'eldrick_session' });
      this.camera.animateToVec({ x: 46, y: 65, z: -80 }, { x: 0, y: 0, z: 150 }, 1.0, 1.4, 'freeLook');
      void new FittingArrival(payload => this.startEldrickFlow(payload)).load(pathId);
    }
  }

  private startEldrickFlow(payload: EldrickPayload): void {
    trackEvent('eldrick_flow_start', {
      fitting_type: payload.meta.fittingType || 'driver',
    });
    // Auto-select the club from the payload
    const clubKey = payload.meta.fittingType === 'iron' ? '7iron' : 'driver';
    this.clubSelect.value = clubKey;
    this.onClubChange();
    this.lastBaselineFormatted = null;
    const handicap = Number(payload.player.handicap ?? 15);
    this.currentHandicap = Number.isFinite(handicap) ? Math.max(-10, Math.min(54, handicap)) : 15;
    this.setInputValue('handicap', this.currentHandicap);

    // Also sync mobile club selector
    const mClubSelect = document.getElementById('m-club-select') as HTMLSelectElement;
    if (mClubSelect) mClubSelect.value = clubKey;

    // Form, renderer, and comparison engine use the same resolved launch values.
    for (const [key, value] of Object.entries(importedLaunch(payload).params)) {
      if (typeof value === 'number') this.setInputValue(key, value);
    }
    this.syncDesktopToMobile();

    // Start the ELDRICK flow in the orchestrator
    this.orchestrator.startFromEldrick(payload);
  }

  private setInputValue(id: string, value: number): void {
    const el = document.getElementById(id) as HTMLInputElement;
    if (el) el.value = String(value);
  }

  // ── Baseline Generation ──────────────────────────────────────────────────

  /**
   * Generate a realistic user baseline for every club from a handicap value.
   */
  private generateBaseline(handicap: number): void {
    const profile = getHandicapProfile(handicap);

    const tourDriver = CLUB_PRESETS['driver'];
    const scaledDriver = scalePreset(tourDriver, 'driver', profile);

    // Wider jitter ranges — makes each randomize feel distinct
    const jitterSpeed = 1 + (Math.random() * 0.12 - 0.06);      // ±6% speed
    const jitterLaunch = (Math.random() * 3 - 1.5);              // ±1.5° launch
    const jitterSpin = 1 + (Math.random() * 0.16 - 0.08);        // ±8% spin
    const jitterFace = (Math.random() * 4 - 2);                   // ±2° face angle
    const jitterPath = (Math.random() * 4 - 2);                   // ±2° club path
    const jitterAttack = (Math.random() * 2 - 1);                 // ±1° attack angle

    this.userBaseline['driver'] = {
      ...scaledDriver,
      clubSpeed: Math.round(scaledDriver.clubSpeed * jitterSpeed),
      ballSpeed: Math.round(scaledDriver.ballSpeed * jitterSpeed),
      launchAngle: Math.round((scaledDriver.launchAngle + jitterLaunch) * 10) / 10,
      spinRate: Math.round((scaledDriver.spinRate * jitterSpin) / 50) * 50,
      faceAngle: Math.round(jitterFace * 10) / 10,
      clubPath: Math.round(jitterPath * 10) / 10,
      attackAngle: Math.round((scaledDriver.attackAngle + jitterAttack) * 10) / 10,
    };

    const driverTourSpeed = tourDriver.clubSpeed;
    const userDriverSpeed = this.userBaseline['driver'].clubSpeed;
    const speedRatio = userDriverSpeed / driverTourSpeed;

    for (const [key, tourPreset] of Object.entries(CLUB_PRESETS)) {
      if (key === 'driver') continue;

      // Per-club jitter — same approach as driver for all metrics
      const csJitter = 1 + (Math.random() * 0.10 - 0.05);       // ±5% speed
      const laJitter = (Math.random() * 2.4 - 1.2);              // ±1.2° launch
      const spJitter = 1 + (Math.random() * 0.12 - 0.06);        // ±6% spin
      const clubFace = Math.round((Math.random() * 3 - 1.5) * 10) / 10;  // ±1.5° face
      const clubPath2 = Math.round((Math.random() * 3 - 1.5) * 10) / 10; // ±1.5° path
      const aaJitter = (Math.random() * 1.6 - 0.8);              // ±0.8° attack

      const clubSpeed = Math.round(tourPreset.clubSpeed * speedRatio * csJitter);
      const tourSmash = tourPreset.ballSpeed / tourPreset.clubSpeed;
      const ballSpeed = Math.round(clubSpeed * tourSmash * profile.smashScale);
      const scaled = scalePreset(tourPreset, key, profile);

      this.userBaseline[key] = {
        ...tourPreset,
        clubSpeed,
        ballSpeed,
        launchAngle: Math.round((scaled.launchAngle + laJitter) * 10) / 10,
        spinRate: Math.round((scaled.spinRate * spJitter) / 50) * 50,
        attackAngle: Math.round((scaled.attackAngle + aaJitter) * 10) / 10,
        clubPath: clubPath2,
        faceAngle: clubFace,
        spinAxis: Math.round((clubFace - clubPath2) * 0.75 * 10) / 10, // D-plane approx
      };
    }
  }

  // ── Gear Specs ───────────────────────────────────────────────────────────

  private getGearSpecs(): GearSpecs {
    return {
      loft: parseFloat((document.getElementById('spec-loft') as HTMLInputElement).value) || 10.5,
      shaftWeight: parseFloat((document.getElementById('spec-shaft-weight') as HTMLInputElement).value) || 65,
      shaftFlex: (document.getElementById('spec-shaft-flex') as HTMLSelectElement).value as ShaftFlex,
      ballCompression: parseFloat((document.getElementById('spec-compression') as HTMLInputElement).value) || 90,
      ballCover: ((document.getElementById('spec-ball-cover') as HTMLSelectElement)?.value || 'urethane') as import('../equipment/types').BallCover,
      ironCategory: (document.getElementById('spec-iron-cat') as HTMLSelectElement).value as IronCategory,
      clubLength: DEFAULT_SPECS.clubLength,
      swingWeight: DEFAULT_SPECS.swingWeight,
      launchProfile: DEFAULT_SPECS.launchProfile,
      headType: DEFAULT_SPECS.headType,
      weightSetting: DEFAULT_SPECS.weightSetting,
    };
  }

  /** Reset spec inputs to defaults. */
  private resetSpecInputs(): void {
    (document.getElementById('spec-loft') as HTMLInputElement).value = String(DEFAULT_SPECS.loft);
    (document.getElementById('spec-shaft-weight') as HTMLInputElement).value = String(DEFAULT_SPECS.shaftWeight);
    (document.getElementById('spec-shaft-flex') as HTMLSelectElement).value = DEFAULT_SPECS.shaftFlex;
    (document.getElementById('spec-compression') as HTMLInputElement).value = String(DEFAULT_SPECS.ballCompression);
    const coverEl = document.getElementById('spec-ball-cover') as HTMLSelectElement;
    if (coverEl) coverEl.value = DEFAULT_SPECS.ballCover;
    (document.getElementById('spec-iron-cat') as HTMLSelectElement).value = DEFAULT_SPECS.ironCategory;
  }

  /** Load a spec config into the gear spec input fields. */
  private loadSpecs(specs: GearSpecs): void {
    (document.getElementById('spec-loft') as HTMLInputElement).value = String(specs.loft);
    (document.getElementById('spec-shaft-weight') as HTMLInputElement).value = String(specs.shaftWeight);
    (document.getElementById('spec-shaft-flex') as HTMLSelectElement).value = specs.shaftFlex;
    (document.getElementById('spec-compression') as HTMLInputElement).value = String(specs.ballCompression);
    const coverEl = document.getElementById('spec-ball-cover') as HTMLSelectElement;
    if (coverEl) coverEl.value = specs.ballCover;
    (document.getElementById('spec-iron-cat') as HTMLSelectElement).value = specs.ironCategory;
    this.onClubChange();
  }

  /** Handle user selecting a spec config (from mobile card tap). */
  private onSelectConfig(results: ComparisonEntry[], idx: number): void {
    const entry = results[idx];
    if (!entry?.specs) return;
    this.selectedSpecEntry = entry;
    this.displayedSpecTotal = parseFloat(entry.formatted.total) || 0;
    this.loadSpecs(entry.specs);
    trackEvent('spec_selected', {
      flow: this.orchestrator.isEldrickFlow ? 'eldrick' : 'standard',
      label: entry.label || `Config ${idx + 1}`,
      total_distance: this.displayedSpecTotal,
      delta: this.displayedSpecTotal - this.displayedBaselineTotal,
      selection_source: this.autoSelectingRecommendation ? 'recommendation' : 'user',
    });
  }

  /** Reset the staged test: clear tracers, reset specs, go back to READY. */
  private resetTest(): void {
    this.orchestrator.reset();
    this.resetSpecInputs();
    this.selectedSpecEntry = null;
    this.selectedGearEntry = null;
    this.selectedBallEntry = null;
    this.displayedBaselineTotal = 0;
    this.displayedSpecTotal = 0;
    this.displayedGearTotal = 0;
    this.displayedBallTotal = 0;
    this.mobileCta.style.display = '';

    // Restore the phase-aware onComplete callback (same as in bindEvents)
    this.simulator.setOnComplete((_result: ShotResult, formatted: Record<string, string>) => {
      const testPhase = this.orchestrator.phase;

      if (testPhase === TestPhase.BaselineFlight) {
        this.showMobileShotData(formatted);
        const clubKey = this.clubSelect.value;
        const rawHcp2 = parseFloat((document.getElementById('handicap') as HTMLInputElement).value);
        const hcp = isNaN(rawHcp2) ? 15 : rawHcp2;
        const clubSpeed = parseFloat((document.getElementById('clubSpeed') as HTMLInputElement).value);
        const ballSpeed = parseFloat((document.getElementById('ballSpeed') as HTMLInputElement).value);
        const launchAngle = parseFloat((document.getElementById('launchAngle') as HTMLInputElement).value);
        const spinRate = parseFloat((document.getElementById('spinRate') as HTMLInputElement).value);
        const spinAxis = parseFloat((document.getElementById('spinAxis') as HTMLInputElement).value);
        const attackAngle = parseFloat((document.getElementById('attackAngle') as HTMLInputElement).value);
        const clubPath = parseFloat((document.getElementById('clubPath') as HTMLInputElement).value);
        const faceAngle = parseFloat((document.getElementById('faceAngle') as HTMLInputElement).value);
        const launchDirection = deriveLaunchDirection(faceAngle, clubPath, launchAngle, attackAngle);
        const params = createSwingParams(clubSpeed, ballSpeed, launchAngle, launchDirection, spinRate, spinAxis, attackAngle, clubPath, faceAngle, 'medium', 'medium');
        const actualBaseline = { ...this.userBaseline[clubKey], clubSpeed, ballSpeed, launchAngle, spinRate, spinAxis, attackAngle, clubPath, faceAngle };
        this.audio.playHitSound();
        this.orchestrator.startMultiBaseline(clubKey, actualBaseline, params, hcp);
      } else {
        this.showMobileShotData(formatted);
      }
    });

    // Re-generate baseline and refresh
    const rawHcp = parseFloat((document.getElementById('handicap') as HTMLInputElement).value);
    const handicap = isNaN(rawHcp) ? 15 : rawHcp;
    this.generateBaseline(handicap);
    this.onClubChange();
  }

  private bindEvents(): void {
    this.hitBtn.addEventListener('click', () => this.onHit());
    this.randomBtn.addEventListener('click', () => this.onRandomize());
    this.clubSelect.addEventListener('change', () => this.onClubChange());

    // Spec inputs and handicap → recompute launch conditions
    const specIds = ['spec-loft', 'spec-shaft-weight', 'spec-shaft-flex', 'spec-compression', 'spec-iron-cat'];
    specIds.forEach(id => {
      const el = document.getElementById(id)!;
      el.addEventListener('input', () => this.onClubChange());
      el.addEventListener('change', () => this.onClubChange());
    });

    const handicapInput = document.getElementById('handicap') as HTMLInputElement;
    handicapInput.addEventListener('input', () => {
      const rawHcp = parseFloat(handicapInput.value);
      const handicap = isNaN(rawHcp) ? 15 : rawHcp;
      this.currentHandicap = handicap;
      this.generateBaseline(handicap);
      this.onClubChange();
    });

    // Auto-derive face-to-path and update spin axis when impact data changes
    const faceAngleInput = document.getElementById('faceAngle') as HTMLInputElement;
    const clubPathInput = document.getElementById('clubPath') as HTMLInputElement;
    const impactChangeHandler = () => this.onImpactDataChange();
    faceAngleInput.addEventListener('input', impactChangeHandler);
    clubPathInput.addEventListener('input', impactChangeHandler);

    // Auto-derive smash factor
    const clubSpeedInput = document.getElementById('clubSpeed') as HTMLInputElement;
    const ballSpeedInput = document.getElementById('ballSpeed') as HTMLInputElement;
    clubSpeedInput.addEventListener('input', () => this.updateSmashFactor());
    ballSpeedInput.addEventListener('input', () => this.updateSmashFactor());

    // Default shot complete callback (baseline shot and free-form shots)
    this.simulator.setOnComplete((_result: ShotResult, formatted: Record<string, string>) => {
      const testPhase = this.orchestrator.phase;

      if (testPhase === TestPhase.BaselineFlight) {
        this.showMobileShotData(formatted);
        const clubKey = this.clubSelect.value;
        const rawHcp = parseFloat((document.getElementById('handicap') as HTMLInputElement).value);
        const handicap = isNaN(rawHcp) ? 15 : rawHcp;
        const clubSpeed = parseFloat((document.getElementById('clubSpeed') as HTMLInputElement).value);
        const ballSpeed = parseFloat((document.getElementById('ballSpeed') as HTMLInputElement).value);
        const launchAngle = parseFloat((document.getElementById('launchAngle') as HTMLInputElement).value);
        const spinRate = parseFloat((document.getElementById('spinRate') as HTMLInputElement).value);
        const spinAxis = parseFloat((document.getElementById('spinAxis') as HTMLInputElement).value);
        const attackAngle = parseFloat((document.getElementById('attackAngle') as HTMLInputElement).value);
        const clubPath = parseFloat((document.getElementById('clubPath') as HTMLInputElement).value);
        const faceAngle = parseFloat((document.getElementById('faceAngle') as HTMLInputElement).value);
        const launchDirection = deriveLaunchDirection(faceAngle, clubPath, launchAngle, attackAngle);
        const params = createSwingParams(clubSpeed, ballSpeed, launchAngle, launchDirection, spinRate, spinAxis, attackAngle, clubPath, faceAngle, 'medium', 'medium');
        const actualBaseline = { ...this.userBaseline[clubKey], clubSpeed, ballSpeed, launchAngle, spinRate, spinAxis, attackAngle, clubPath, faceAngle };
        this.audio.playHitSound();
        this.orchestrator.startMultiBaseline(clubKey, actualBaseline, params, handicap);
      } else {
        this.showMobileShotData(formatted);
      }
    });
  }

  private onImpactDataChange(): void {
    const faceAngle = parseFloat((document.getElementById('faceAngle') as HTMLInputElement).value) || 0;
    const clubPath = parseFloat((document.getElementById('clubPath') as HTMLInputElement).value) || 0;
    const launchAngle = parseFloat((document.getElementById('launchAngle') as HTMLInputElement).value) || 10;
    const attackAngle = parseFloat((document.getElementById('attackAngle') as HTMLInputElement).value) || 0;

    const faceToPath = computeFaceToPath(faceAngle, clubPath);
    document.getElementById('faceToPath')!.textContent = `${faceToPath.toFixed(1)}\u00B0`;

    const spinAxis = deriveSpinAxisFromImpact(faceAngle, clubPath, launchAngle, attackAngle);
    (document.getElementById('spinAxis') as HTMLInputElement).value = String(spinAxis);

    const launchDir = deriveLaunchDirection(faceAngle, clubPath, launchAngle, attackAngle);
    (document.getElementById('launchDir') as HTMLInputElement).value = String(Math.round(launchDir * 10) / 10);
  }

  private updateSmashFactor(): void {
    const clubSpeed = parseFloat((document.getElementById('clubSpeed') as HTMLInputElement).value) || 0;
    const ballSpeed = parseFloat((document.getElementById('ballSpeed') as HTMLInputElement).value) || 0;

    if (clubSpeed > 0) {
      const smash = ballSpeed / clubSpeed;
      document.getElementById('smash-factor')!.textContent = `SMASH ${smash.toFixed(2)}`;
    } else {
      document.getElementById('smash-factor')!.textContent = 'SMASH --';
    }
  }

  setCurrentAngle(angle: 'behind' | 'side' | 'top'): void {
    this.currentAngle = angle;
  }

  private onHit(): void {
    const simPhase = this.simulator.getPhase();
    const testPhase = this.orchestrator.phase;

    // ── Phase-aware CTA routing ──

    // In Results phase with a selected entry → start gear test
    if (testPhase === TestPhase.Results && this.selectedSpecEntry) {
      this.orchestrator.startGearTest(this.selectedSpecEntry);
      return;
    }

    // In GearResults or Summary → reset for a new test
    if (testPhase === TestPhase.GearResults || testPhase === TestPhase.Summary) {
      this.resetTest();
      return;
    }

    // Normal shot (baseline or free-form) — guard against mid-flight clicks
    if (simPhase === SimPhase.Flight || simPhase === SimPhase.Ground) return;

    // Read all input fields (before startBaseline so we pass actual values)
    const clubSpeed = parseFloat((document.getElementById('clubSpeed') as HTMLInputElement).value);
    const ballSpeed = parseFloat((document.getElementById('ballSpeed') as HTMLInputElement).value);
    const launchAngle = parseFloat((document.getElementById('launchAngle') as HTMLInputElement).value);
    const launchDir = parseFloat((document.getElementById('launchDir') as HTMLInputElement).value);
    const spinRate = parseFloat((document.getElementById('spinRate') as HTMLInputElement).value);
    const spinAxis = parseFloat((document.getElementById('spinAxis') as HTMLInputElement).value);
    const attackAngle = parseFloat((document.getElementById('attackAngle') as HTMLInputElement).value);
    const clubPath = parseFloat((document.getElementById('clubPath') as HTMLInputElement).value);
    const faceAngle = parseFloat((document.getElementById('faceAngle') as HTMLInputElement).value);

    // If in READY phase, this is the baseline shot — tell orchestrator
    // Use actual input values (not random userBaseline) so comparisons are accurate
    if (testPhase === TestPhase.Ready) {
      const clubKey = this.clubSelect.value;
      const baseline = this.userBaseline[clubKey];
      if (baseline) {
        this.orchestrator.startBaseline(clubKey, {
          ...baseline,
          clubSpeed, ballSpeed, launchAngle, spinRate, spinAxis, attackAngle, clubPath, faceAngle,
        });
      }
    }
    const tempo = (document.getElementById('tempo') as HTMLSelectElement).value as Tempo;
    const transition = (document.getElementById('transition') as HTMLSelectElement).value as Transition;

    const params = createSwingParams(
      clubSpeed, ballSpeed, launchAngle, launchDir,
      spinRate, spinAxis,
      attackAngle, clubPath, faceAngle,
      tempo, transition,
    );

    this.audio.playHitSound();
    this.simulator.launch(params, this.currentAngle);
    this.camera.beginFlightPresentation();
    this.camera.setFlightPoints(predictFlightFrame(params, this.simulator.getEnvironment()));
    this.hitBtn.disabled = true;

    const checkDone = setInterval(() => {
      if (this.simulator.getPhase() === SimPhase.Rest) {
        if (this.orchestrator.phase !== TestPhase.Comparing &&
            this.orchestrator.phase !== TestPhase.BaselineDone &&
            this.orchestrator.phase !== TestPhase.BaselineMultiFlight &&
            this.orchestrator.phase !== TestPhase.GearTesting) {
          this.hitBtn.disabled = false;
        }
        clearInterval(checkDone);
      }
    }, 100);
  }

  private onRandomize(): void {
    void this.randomizeFromTrackMan();
  }

  private async randomizeFromTrackMan(): Promise<void> {
    const { TRACKMAN_AVERAGES } = await import('../validation/validateRealShots');
    const clubKey = this.clubSelect.value;

    // Map our club keys to TrackMan club abbreviations
    const clubMap: Record<string, string[]> = {
      'driver': ['Dr'],
      '3wood': ['3W'],
      '5wood': ['5W', '3W'],
      '4hybrid': ['3H', '4H'],
      '4iron': ['4H', '7i'],
      '5iron': ['7i'],
      '6iron': ['7i'],
      '7iron': ['7i'],
      '8iron': ['7i'],
      '9iron': ['7i'],
      'pitching-wedge': ['50W', '52W'],
      'gap-wedge': ['50W', '52W'],
      'sand-wedge': ['50W', '52W', '60W'],
      'lob-wedge': ['60W'],
    };

    const tmClubs = clubMap[clubKey] || ['Dr'];
    const matching = TRACKMAN_AVERAGES.filter(t => tmClubs.includes(t.club));

    if (matching.length > 0) {
      // Pick a random real player config
      const shot = matching[Math.floor(Math.random() * matching.length)];

      // Add slight jitter to make each randomize feel distinct (±3%)
      const jitter = () => 1 + (Math.random() * 0.06 - 0.03);
      const smash = shot.ballSpeed / shot.clubSpeed;
      const faceJitter = (Math.random() * 4 - 2);   // ±2° face angle
      const pathJitter = (Math.random() * 4 - 2);    // ±2° path

      this.userBaseline[clubKey] = {
        name: clubKey,
        clubSpeed: Math.round(shot.clubSpeed * jitter()),
        ballSpeed: Math.round(shot.clubSpeed * jitter() * smash),
        launchAngle: Math.round((shot.launchAngle + (Math.random() * 2 - 1)) * 10) / 10,
        spinRate: Math.round(shot.spinRate * jitter() / 50) * 50,
        attackAngle: Math.round((shot.attackAngle + (Math.random() * 1 - 0.5)) * 10) / 10,
        faceAngle: Math.round(faceJitter * 10) / 10,
        clubPath: Math.round(pathJitter * 10) / 10,
        spinAxis: Math.round((faceJitter - pathJitter) * 0.75 * 10) / 10,
      };

      // Update the swing source label — anonymize player name to initials
      const initials = shot.player.split(/\s+/).map(w => w[0]).join('').toUpperCase();
      const sourceEl = document.querySelector('.m-swing-source-text');
      if (sourceEl) sourceEl.textContent = `User ${initials} — ${shot.head} (TrackMan)`;
    } else {
      // Fallback to handicap-based generation
      const rawHcp = parseFloat((document.getElementById('handicap') as HTMLInputElement).value);
      const handicap = isNaN(rawHcp) ? 15 : rawHcp;
      this.generateBaseline(handicap);
    }

    this.onClubChange();
  }

  /** Apply a speed bracket profile — auto-fills all swing data from realistic defaults. */
  private applySpeedBracket(bracket: string): void {
    const profiles: Record<string, { speed: number; ball: number; launch: number; spin: number; attack: number; hcp: number; label: string }> = {
      slow:     { speed: 78,  ball: 108, launch: 14.2, spin: 3800, attack: -1.5, hcp: 24, label: 'Beginner / Senior profile' },
      moderate: { speed: 90,  ball: 130, launch: 12.8, spin: 3200, attack: -0.5, hcp: 15, label: 'Recreational profile' },
      fast:     { speed: 100, ball: 145, launch: 11.5, spin: 2700, attack: 1.0,  hcp: 8,  label: 'Competitive profile' },
      tour:     { speed: 110, ball: 160, launch: 10.5, spin: 2400, attack: 2.0,  hcp: 3,  label: 'Tour / Scratch profile' },
    };
    const p = profiles[bracket] || profiles['moderate'];

    // A selected profile is a stable center; shot-to-shot variation is applied
    // when the baseline fires. Custom face/path inputs can add a deliberate bias.
    const clubKey = this.clubSelect.value;
    this.userBaseline[clubKey] = {
      name: clubKey,
      clubSpeed: p.speed,
      ballSpeed: p.ball,
      launchAngle: p.launch,
      spinRate: p.spin,
      attackAngle: p.attack,
      faceAngle: 0,
      clubPath: 0,
      spinAxis: 0,
    };

    // Update handicap
    this.currentHandicap = p.hcp;
    (document.getElementById('handicap') as HTMLInputElement).value = String(p.hcp);
    (document.getElementById('m-handicap') as HTMLInputElement).value = String(p.hcp);

    // Update source text
    const sourceEl = document.getElementById('m-swing-source-text');
    if (sourceEl) sourceEl.textContent = `${p.label} selected`;

    this.onClubChange();
  }

  private onClubChange(): void {
    const clubKey = this.clubSelect.value;
    const baseline = this.userBaseline[clubKey];
    if (!baseline) return;

    const specs = this.getGearSpecs();
    const mods = computeSpecModifiers(specs, clubKey, baseline.clubSpeed);
    const adjusted = applyModifiers(baseline, mods);

    (document.getElementById('clubSpeed') as HTMLInputElement).value = String(baseline.clubSpeed);
    (document.getElementById('attackAngle') as HTMLInputElement).value = String(baseline.attackAngle);
    (document.getElementById('clubPath') as HTMLInputElement).value = String(baseline.clubPath);
    (document.getElementById('faceAngle') as HTMLInputElement).value = String(baseline.faceAngle);
    const spinAxis = deriveSpinAxisFromImpact(
      baseline.faceAngle, baseline.clubPath, adjusted.launchAngle, baseline.attackAngle,
    );
    const launchDirection = deriveLaunchDirection(
      baseline.faceAngle, baseline.clubPath, adjusted.launchAngle, baseline.attackAngle,
    );
    (document.getElementById('spinAxis') as HTMLInputElement).value = String(spinAxis);
    (document.getElementById('launchDir') as HTMLInputElement).value = String(Math.round(launchDirection * 10) / 10);

    (document.getElementById('ballSpeed') as HTMLInputElement).value = String(adjusted.ballSpeed);
    (document.getElementById('launchAngle') as HTMLInputElement).value = String(adjusted.launchAngle);
    (document.getElementById('spinRate') as HTMLInputElement).value = String(adjusted.spinRate);

    const faceToPath = computeFaceToPath(baseline.faceAngle, baseline.clubPath);
    document.getElementById('faceToPath')!.textContent = `${faceToPath.toFixed(1)}\u00B0`;

    this.updateSmashFactor();
    this.updateShotShapePreview();
  }

  /** Update the overhead shot shape dispersion preview SVG. */
  private updateShotShapePreview(): void {
    const container = document.getElementById('shot-shape-container');
    if (!container) return;

    const faceAngle = parseFloat((document.getElementById('faceAngle') as HTMLInputElement).value) || 0;
    const clubPath = parseFloat((document.getElementById('clubPath') as HTMLInputElement).value) || 0;
    const launchAngle = parseFloat((document.getElementById('launchAngle') as HTMLInputElement).value) || 12;
    const attackAngle = parseFloat((document.getElementById('attackAngle') as HTMLInputElement).value) || -1;
    const ballSpeed = parseFloat((document.getElementById('ballSpeed') as HTMLInputElement).value) || 130;
    const spinRate = parseFloat((document.getElementById('spinRate') as HTMLInputElement).value) || 2500;
    const rawHandicap = parseFloat((document.getElementById('handicap') as HTMLInputElement).value);
    const handicap = Number.isFinite(rawHandicap) ? rawHandicap : 15;

    const svg = renderShotShape({
      faceAngle, clubPath, launchAngle, attackAngle,
      ballSpeed, spinRate, handicap,
    });

    container.innerHTML = '';
    container.appendChild(svg);
  }

  hideLoading(): void {
    this.loadingEl.style.opacity = '0';
    setTimeout(() => {
      this.loadingEl.style.display = 'none';
    }, 500);
  }

  // ════════════════════════════════════════════════════════════════════════
  //  MOBILE UI
  // ════════════════════════════════════════════════════════════════════════

  // ── Sheet State Controller ──

  /** Set the bottom sheet to a specific height state. */
  private setSheetState(state: 'full' | 'half' | 'flight' | 'peek' | 'mini' | 'hidden'): void {
    this.mobileSheetState = state;
    this.mobileSheet.setAttribute('data-sheet', state);
    // Show brand when canvas is visible, hide when sheet covers screen
    document.getElementById('mobile-brand')?.classList.toggle('hidden', state === 'full');
  }

  private enterMobileShotPresentation(
    clubName: string,
    subtitle: string,
    showLeaderboard = false,
    phase: 'baseline' | 'spec' | 'gear' | 'ball' = 'baseline',
  ): void {
    // A choice from the previous stage must not remain isolated in the next test.
    this.orchestrator.highlightShot(-1);
    this.setSheetState(showLeaderboard ? 'flight' : 'peek');
    if (innerHeight < 700 && innerWidth < 900) this.setSheetState('peek');
    this.setSheetHeader('WATCH THE SHOT', clubName, subtitle);
    this.showCameraTabs();
    this.camera.beginFlightPresentation();
    this.camera.setMobileDownrangeView(true);
    document.querySelectorAll('#mobile-camera-tabs .cam-tab').forEach(tab => {
      const active = tab.getAttribute('data-cam') === 'behind';
      tab.classList.toggle('active', active);
      tab.setAttribute('aria-pressed', String(active));
    });
    void phase;
  }

  private exitMobileShotPresentation(): void {
    this.setSheetState('half');
  }

  /** Bind drag gesture on the sheet handle + header for iOS-style drawer behavior.
   *  Supports both touch (mobile) and mouse (desktop/localhost testing). */
  private bindSheetDrag(): void {
    const handle = document.getElementById('m-sheet-handle')!;
    const header = document.querySelector('.mobile-sheet-header') as HTMLElement;
    const sheet = this.mobileSheet;

    let startY = 0;
    let startHeight = 0;
    let dragging = false;
    let didDrag = false; // distinguish drag from tap

    const canDrag = () => this.mobileSheetState !== 'full' && this.mobileSheetState !== 'hidden';

    const beginDrag = (clientY: number) => {
      if (!canDrag()) return;
      startY = clientY;
      startHeight = sheet.getBoundingClientRect().height;
      dragging = true;
      didDrag = false;
      sheet.classList.add('dragging');
    };

    const moveDrag = (clientY: number) => {
      if (!dragging) return;
      const dy = startY - clientY;
      if (Math.abs(dy) > 4) didDrag = true;
      const newHeight = Math.max(56, Math.min(window.innerHeight * 0.75, startHeight + dy));
      sheet.style.height = newHeight + 'px';
    };

    const endDrag = () => {
      if (!dragging) return;
      const finalH = sheet.getBoundingClientRect().height;
      dragging = false;
      sheet.classList.remove('dragging');
      sheet.style.height = '';

      if (!didDrag) return; // was a tap, not a drag — let click handler deal with it

      // Snap to nearest state
      if (finalH < 120) {
        this.setSheetState('peek');
      } else {
        this.setSheetState('half');
      }
    };

    // ── Touch events (mobile) ──
    const dragTargets = [handle, header];
    for (const el of dragTargets) {
      el.addEventListener('touchstart', (e: TouchEvent) => beginDrag(e.touches[0].clientY), { passive: true });
    }
    document.addEventListener('touchmove', (e: TouchEvent) => {
      if (!dragging) return;
      e.preventDefault();
      moveDrag(e.touches[0].clientY);
    }, { passive: false });
    document.addEventListener('touchend', () => endDrag());

    // ── Mouse events (desktop / localhost testing) ──
    for (const el of dragTargets) {
      el.addEventListener('mousedown', (e: MouseEvent) => {
        e.preventDefault();
        beginDrag(e.clientY);
      });
    }
    document.addEventListener('mousemove', (e: MouseEvent) => {
      if (!dragging) return;
      e.preventDefault();
      moveDrag(e.clientY);
    });
    document.addEventListener('mouseup', () => endDrag());

    // ── Tap to toggle (click on handle or header) ──
    for (const el of dragTargets) {
      el.addEventListener('click', () => {
        if (!canDrag() || didDrag) return;
        if (this.mobileSheetState === 'peek' || this.mobileSheetState === 'mini') {
          this.setSheetState('half');
        } else {
          this.setSheetState('peek');
        }
      });
    }
  }

  /**
   * Tap-to-inspect: tap a shot in the 3D world to see its stats.
   * Screen-space hit targets follow the visible arc, at the same size on every device.
   */
  private bindTapToInspect(): void {
    const canvas = document.getElementById('renderCanvas') as HTMLCanvasElement;
    const tooltip = document.getElementById('shot-tooltip')!;
    const tooltipLabel = tooltip.querySelector('.shot-tooltip-label') as HTMLElement;
    const tooltipStats = tooltip.querySelector('.shot-tooltip-stats') as HTMLElement;

    let tooltipTimeout: ReturnType<typeof setTimeout> | null = null;

    const showTooltip = (screenX: number, screenY: number) => {
      // Only inspect during results phases
      const phase = this.orchestrator.phase;
      if (phase !== TestPhase.Results && phase !== TestPhase.GearResults && phase !== TestPhase.BallResults) return;

      const cam = this.camera.getCamera();
      const scene = cam.getScene();
      const rect = canvas.getBoundingClientRect();
      const viewport = cam.viewport.toGlobal(rect.width, rect.height);
      const hit = this.orchestrator.findShotOnScreen({ x: screenX - rect.left, y: screenY - rect.top }, p => {
        const projected = Vector3.Project(new Vector3(p.x, p.y, p.z), Matrix.Identity(), scene.getTransformMatrix(), viewport);
        return projected.z >= 0 && projected.z <= 1 ? projected : null;
      });
      if (!hit) {
        tooltip.classList.remove('visible');
        return;
      }

      // Arc, card, pinned comparison and continue action share one identity.
      const type = phase === TestPhase.BallResults ? 'ball' : phase === TestPhase.GearResults ? 'gear' : 'spec';
      const row = document.querySelector<HTMLTableRowElement>(`#m-${type}-table-rows tr[data-config-index="${hit.configIndex}"]`);
      if (row) row.click(); else this.orchestrator.highlightShot(hit.configIndex);

      // Show tooltip
      const f = hit.entry.formatted;
      tooltipLabel.textContent = hit.entry.label;
      tooltipLabel.style.color = hit.entry.colorHex;
      tooltipStats.innerHTML = `
        ${f.carry} <span>carry</span> &middot; ${f.total} <span>total</span><br>
        ${f.offline} <span>offline</span> &middot; ${f.landing}&deg; <span>land</span>
      `;
      tooltip.style.left = `${screenX}px`;
      tooltip.style.top = `${screenY}px`;
      tooltip.style.display = '';
      const tooltipWidth = tooltip.getBoundingClientRect().width;
      tooltip.style.left = `${Math.max(tooltipWidth / 2 + 12, Math.min(innerWidth - tooltipWidth / 2 - 12, screenX))}px`;
      requestAnimationFrame(() => tooltip.classList.add('visible'));

      // Auto-hide after 3s
      if (tooltipTimeout) clearTimeout(tooltipTimeout);
      tooltipTimeout = setTimeout(() => {
        tooltip.classList.remove('visible');
      }, 3000);
    };

    // Touch tap (distinguish from drag via distance threshold)
    let touchStartX = 0, touchStartY = 0;
    canvas.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    }, { passive: true });

    canvas.addEventListener('touchend', (e) => {
      if (e.changedTouches.length !== 1) return;
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (Math.sqrt(dx * dx + dy * dy) < 15) {
        showTooltip(touchStartX, touchStartY);
      }
    }, { passive: true });

    // Mouse click (desktop)
    let mouseStart = { x: 0, y: 0 };
    canvas.addEventListener('mousedown', e => { mouseStart = { x: e.clientX, y: e.clientY }; });
    canvas.addEventListener('click', (e) => {
      if (Math.hypot(e.clientX - mouseStart.x, e.clientY - mouseStart.y) < 8) showTooltip(e.clientX, e.clientY);
    });
  }

  /** Switch the visible content section inside the sheet. */
  private showSheetContent(id: 'club' | 'swing' | 'baseline-table' | 'specs' | 'gear' | 'ball' | 'summary'): void {
    this.mobileCurrentContent = id;
    const body = this.mobileSheet.querySelector<HTMLElement>('.mobile-sheet-body');
    if (body) { body.scrollTop = 0; body.scrollLeft = 0; }
    this.mobileSheet.querySelectorAll('.sheet-content').forEach(el => el.classList.remove('active'));
    document.getElementById(`sheet-${id}`)?.classList.add('active');
  }

  /** Update the sheet header (phase label, club name, subtitle). */
  private setSheetHeader(phase: string, club?: string, subtitle?: string): void {
    document.getElementById('m-sheet-phase')!.textContent = phase;
    document.getElementById('m-sheet-club')!.textContent = club || '';
    document.getElementById('m-sheet-subtitle')!.textContent = subtitle || '';
  }

  /** Update the single mobile CTA button. */
  private setMobileCta(text: string, disabled: boolean, green: boolean): void {
    this.mobileCta.textContent = text;
    this.mobileCta.setAttribute('aria-label', text);
    this.mobileCta.disabled = disabled;
    this.mobileCta.classList.toggle('green', green);
    this.mobileCta.classList.toggle('needs-selection', disabled && text === 'CHOOSE ONE ABOVE \u2191');
  }

  private setSelectionCta(type: 'spec' | 'gear' | 'ball', entry: ComparisonEntry): void {
    const max = type === 'gear' ? 20 : 24;
    const label = entry.label.length > max ? `${entry.label.slice(0, max - 1)}…` : entry.label;
    const prefix = type === 'ball' ? 'FINISH WITH' : 'CONTINUE WITH';
    this.setMobileCta(`${prefix} ${label.toUpperCase()} →`, false, true);
    this.mobileCta.setAttribute('aria-label', `${prefix.toLowerCase()} ${entry.label}`);
  }

  /** Show the randomize button (only in setup phase). */
  private showRandomBtn(visible: boolean): void {
    document.getElementById('m-random-btn')!.style.display = visible ? 'flex' : 'none';
  }

  // ── Progress Pill ──

  private showProgressPill(label: string, _current: number, total: number): void {
    const pill = document.getElementById('mobile-progress-pill')!;
    document.getElementById('m-pill-label')!.textContent = label;
    document.getElementById('m-pill-count')!.textContent = this.progressStatusText(label, 0, total);
    (document.getElementById('m-pill-fill') as HTMLElement).style.width = '0%';
    pill.classList.add('visible');
  }

  private updateProgressPill(current: number, total: number): void {
    const label = document.getElementById('m-pill-label')!.textContent || '';
    document.getElementById('m-pill-count')!.textContent = this.progressStatusText(label, current, total);
    const pct = total > 0 ? (current / total) * 100 : 0;
    (document.getElementById('m-pill-fill') as HTMLElement).style.width = `${pct}%`;
  }

  /** Contextual status text for the progress pill. */
  private progressStatusText(label: string, current: number, total: number): string {
    void label;
    if (current >= total) {
      return 'Analyzing results...';
    }
    return `${current} / ${total} complete`;
  }

  private hideProgressPill(): void {
    document.getElementById('mobile-progress-pill')!.classList.remove('visible');
  }

  // ── Hero Metric Flash ──

  private showHeroFlash(value: string, unit: string): void {
    const el = document.getElementById('mobile-hero-flash')!;
    document.getElementById('m-hero-value')!.textContent = value;
    document.getElementById('m-hero-unit')!.textContent = unit;
    el.classList.remove('fading');
    el.classList.add('visible');

    // Hold 1.5s then fade out
    if (this.heroTimer) clearTimeout(this.heroTimer);
    this.heroTimer = setTimeout(() => {
      el.classList.remove('visible');
      el.classList.add('fading');
      // After fade animation completes, reset
      setTimeout(() => {
        el.classList.remove('fading');
        el.style.opacity = '0';
      }, 400);
    }, 1500);
  }

  // ── Camera Tabs ──

  private showCameraTabs(): void {
    document.getElementById('mobile-camera-tabs')!.classList.add('visible');
  }

  private hideCameraTabs(): void {
    document.getElementById('mobile-camera-tabs')!.classList.remove('visible');
  }

  /** Get the current club name for display. */
  private getClubName(): string {
    const clubKey = this.orchestrator.getClubKey() || this.clubSelect.value;
    return CLUB_PRESETS[clubKey]?.name || clubKey;
  }

  /** Mobile phase handler — uses single bottom sheet with height states. */
  private onTestPhaseChangeMobile(phase: TestPhase): void {
    this.mobileRoot.dataset.phase = phase;
    this.updateFittingPace();
    const clubName = this.getClubName();
    const inspecting = [TestPhase.Results, TestPhase.GearResults, TestPhase.BallResults].includes(phase);
    document.getElementById('flight-interaction-hint')!.textContent = inspecting
      ? 'Tap a flight for stats · Drag to orbit · Pinch to zoom'
      : 'Drag to orbit · Pinch / scroll to zoom';
    const specCount = this.orchestrator.getSpecCount();

    switch (phase) {
      case TestPhase.Ready:
        this.mobileSetupStep = 'club';
        this.setSheetState('full');
        this.showSheetContent('club');
        this.setSheetHeader('PICK YOUR CLUB');
        this.setMobileCta('NEXT \u2192', false, false);
        this.showRandomBtn(false);
        this.hideCameraTabs();
        this.hideProgressPill();
        // Cinematic intro flyby on first load
        this.camera.flybyIntro();
        break;

      case TestPhase.BaselineFlight:
        this.showSheetContent('baseline-table');
        this.enterMobileShotPresentation(clubName, 'Capturing your launch', false, 'baseline');
        this.setMobileCta('HITTING BASELINE\u2026', true, false);
        this.showRandomBtn(false);
        break;

      case TestPhase.BaselineMultiFlight:
        this.showSheetContent('baseline-table');
        this.enterMobileShotPresentation(clubName, 'Hitting 10-shot baseline', false, 'baseline');
        this.buildBaselineTableSkeleton();
        this.setMobileCta('HITTING BASELINE\u2026', true, false);
        this.showProgressPill('BASELINE', 0, 10);
        this.showRandomBtn(false);
        this.audio.playHitSound();
        break;

      case TestPhase.BaselineDone:
        this.hideProgressPill();
        if (this.orchestrator.isEldrickFlow) {
          // Show baseline-table content (for compiled card) and camera
          this.showSheetContent('baseline-table');
          this.showCameraTabs();
          this.camera.beginFlightPresentation();
        }
        this.handleBaselineDone(clubName);
        break;

      case TestPhase.Comparing:
        this.specConfigsCompleted = 0;
        this.showSheetContent('specs');
        if (this.orchestrator.isEldrickFlow) {
          this.enterMobileShotPresentation(clubName, `Exploring your fitting across ${specCount} simulated configs`, true, 'spec');
        } else {
          this.enterMobileShotPresentation(clubName, `Testing ${specCount} spec configurations`, true, 'spec');
        }
        this.setMobileCta('TESTING SPECS\u2026', true, false);
        this.showProgressPill('SPECS', 0, specCount);
        this.buildMobileSpecsSkeleton();
        this.audio.playHitSound();
        break;

      case TestPhase.Results: {
        this.selectedSpecEntry = null;
        this.hideProgressPill();
        this.exitMobileShotPresentation();
        // Hide leaderboard immediately to prevent flash before table renders
        const specLbEl = document.getElementById('m-spec-leaderboard');
        if (specLbEl) specLbEl.style.display = 'none';
        if (this.orchestrator.isEldrickFlow) {
          this.setSheetHeader('SPEC COMPARISON', clubName, 'Core recommendation selected · explore the tradeoffs');
        } else {
          this.setSheetHeader('SPEC RESULTS', clubName, 'Same swings · different setups');
        }
        this.setMobileCta('CHOOSE ONE ABOVE \u2191', true, false);
        break;
      }

      case TestPhase.GearTesting:
        this.gearConfigsCompleted = 0;
        this.showSheetContent('gear');
        this.enterMobileShotPresentation(clubName, 'Testing real driver + shaft combos', true, 'gear');
        this.setMobileCta('TESTING GEAR\u2026', true, false);
        this.showProgressPill('GEAR', 0, 10);
        this.buildMobileGearSkeleton();
        this.audio.playHitSound();
        break;

      case TestPhase.GearResults: {
        this.selectedGearEntry = null;
        this.hideProgressPill();
        this.exitMobileShotPresentation();
        // Hide leaderboard immediately to prevent flash before table renders
        const gearLbEl = document.getElementById('m-gear-leaderboard');
        if (gearLbEl) gearLbEl.style.display = 'none';
        this.showSheetContent('gear');
        this.setSheetHeader('EQUIPMENT RESULTS', clubName, 'Same swings · different setups');
        this.setMobileCta('CHOOSE ONE ABOVE \u2191', true, false);
        break;
      }

      case TestPhase.BallTesting: {
        this.ballConfigsCompleted = 0;
        const ballCount = this.orchestrator.getBallConfigMeta().length;
        this.showSheetContent('ball');
        this.enterMobileShotPresentation(clubName, `Testing ${ballCount} ball models`, true, 'ball');
        this.setMobileCta('TESTING BALLS\u2026', true, false);
        this.showProgressPill('BALLS', 0, ballCount);
        this.buildMobileBallSkeleton();
        this.audio.playHitSound();
        break;
      }

      case TestPhase.BallResults: {
        this.selectedBallEntry = null;
        this.hideProgressPill();
        this.exitMobileShotPresentation();
        // Hide leaderboard immediately to prevent flash before table renders
        const ballLbEl = document.getElementById('m-ball-leaderboard');
        if (ballLbEl) ballLbEl.style.display = 'none';
        this.showSheetContent('ball');
        this.setSheetHeader('BALL RESULTS', clubName, 'Same swings · different setups');
        this.setMobileCta('CHOOSE ONE ABOVE \u2191', true, false);
        break;
      }

      case TestPhase.Summary:
        this.hideCameraTabs();
        this.hideProgressPill();
        this.showSheetContent('summary');
        this.setSheetState('full');
        this.setSheetHeader(this.orchestrator.isEldrickFlow ? 'YOUR FITTING RESULTS' : 'RESULTS', clubName);
        this.buildSummaryView();
        // Hide main CTA — summary has its own action buttons
        this.setMobileCta('', true, false);
        this.mobileCta.style.display = 'none';
        break;
    }
  }

  /** Handle baseline complete — show AVG row and rise sheet. */
  private handleBaselineDone(clubName: string): void {
    trackEvent('baseline_complete', {
      flow: this.orchestrator.isEldrickFlow ? 'eldrick' : 'standard',
      club: clubName,
      total_distance: parseFloat(this.lastBaselineFormatted?.total || '0'),
    });
    if (this.orchestrator.isEldrickFlow) {
      // Let the customer watch their imported launch before exploring alternatives.
      this.showEldrickCaptureCard();
      this.displayedBaselineTotal = parseFloat(this.lastBaselineFormatted?.total || '0');
      setTimeout(() => {
        this.camera.setMobileDownrangeView(true);
        this.setSheetState('peek');
        this.setSheetHeader('YOUR BALL FLIGHT', clubName, 'Your Core inputs. Simulated on the Eldrick range.');
        this.setMobileCta('WATCHING YOUR FLIGHT…', true, false);
      }, 300);
      return;
    }
    this.showBaselineAverage();
    this.displayedBaselineTotal = parseFloat(this.lastBaselineFormatted?.total || '0');
    this.showHeroFlash(this.lastBaselineFormatted?.carry || '--', 'yds');
    setTimeout(() => {
      this.setSheetState('half');
      this.setSheetHeader('BASELINE', clubName, 'Average of 10 shots');
      this.setMobileCta('TRY SOME SPECS', false, true);
      // Scroll compiled card into view
      document.getElementById('m-baseline-compiled')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 500);
  }

  /** Re-hit baseline — reset and re-fire 10 baseline shots with current swing data. */
  private rehitBaseline(): void {
    if (this.orchestrator.phase !== TestPhase.BaselineDone) return;
    trackEvent('rehit_baseline');

    // Hide compiled card and reset table
    const compiled = document.getElementById('m-baseline-compiled')!;
    compiled.style.display = 'none';

    // Read current swing params from the form
    const clubKey = this.clubSelect.value;
    const rawHcp = parseFloat((document.getElementById('handicap') as HTMLInputElement).value);
    const handicap = isNaN(rawHcp) ? 15 : rawHcp;
    const clubSpeed = parseFloat((document.getElementById('clubSpeed') as HTMLInputElement).value);
    const ballSpeed = parseFloat((document.getElementById('ballSpeed') as HTMLInputElement).value);
    const launchAngle = parseFloat((document.getElementById('launchAngle') as HTMLInputElement).value);
    const spinRate = parseFloat((document.getElementById('spinRate') as HTMLInputElement).value);
    const spinAxis = parseFloat((document.getElementById('spinAxis') as HTMLInputElement).value);
    const attackAngle = parseFloat((document.getElementById('attackAngle') as HTMLInputElement).value);
    const clubPath = parseFloat((document.getElementById('clubPath') as HTMLInputElement).value);
    const faceAngle = parseFloat((document.getElementById('faceAngle') as HTMLInputElement).value);
    const launchDirection = deriveLaunchDirection(faceAngle, clubPath, launchAngle, attackAngle);
    const params = createSwingParams(clubSpeed, ballSpeed, launchAngle, launchDirection, spinRate, spinAxis, attackAngle, clubPath, faceAngle, 'medium', 'medium');
    const actualBaseline = { ...this.userBaseline[clubKey], clubSpeed, ballSpeed, launchAngle, spinRate, spinAxis, attackAngle, clubPath, faceAngle };

    // Re-launch baseline
    this.orchestrator.startBaseline(clubKey, actualBaseline);
    this.orchestrator.startMultiBaseline(clubKey, actualBaseline, params, handicap);
  }

  /** Build 10 skeleton rows in the baseline table. */
  private buildBaselineTableSkeleton(): void {
    const tbody = document.getElementById('m-baseline-rows')!;
    tbody.innerHTML = '';
    for (let i = 0; i < 10; i++) {
      const tr = document.createElement('tr');
      tr.className = 'skeleton';
      tr.id = `m-brow-${i}`;
      tr.innerHTML = `<td>${i + 1}</td><td>--</td><td>--</td><td>--</td><td>--</td><td>--</td><td>--</td>`;
      tbody.appendChild(tr);
    }
    const avg = document.getElementById('m-baseline-avg')!;
    avg.style.display = 'none';
    avg.classList.remove('visible');
    // Hide compiled baseline card on reset
    const compiled = document.getElementById('m-baseline-compiled');
    if (compiled) compiled.style.display = 'none';
  }

  /** Update a single row in the baseline table when a shot completes. */
  private updateBaselineTableRow(index: number, formatted: Record<string, string>, spinRate: number): void {
    const tr = document.getElementById(`m-brow-${index}`);
    if (!tr) return;
    tr.classList.remove('skeleton');
    tr.classList.add('completed');
    const spinDisplay = spinRate >= 1000 ? (spinRate / 1000).toFixed(1) + 'k' : String(Math.round(spinRate));
    tr.innerHTML = `<td>${index + 1}</td><td>${formatted.carry}</td><td>${formatted.total}</td><td>${formatted.height}</td><td>${spinDisplay}</td><td>${formatted.landing}\u00b0</td><td>${formatted.offline}</td>`;
  }

  /** Show the AVG row in the baseline table + compiled baseline card. */
  private showBaselineAverage(): void {
    const formatted = this.lastBaselineFormatted;
    if (!formatted) return;
    document.getElementById('m-bavg-carry')!.textContent = formatted.carry;
    document.getElementById('m-bavg-total')!.textContent = formatted.total;
    document.getElementById('m-bavg-height')!.textContent = formatted.height;
    document.getElementById('m-bavg-spin')!.textContent = formatted.spin || '--';
    document.getElementById('m-bavg-land')!.textContent = formatted.landing + '\u00b0';
    document.getElementById('m-bavg-offline')!.textContent = formatted.offline;
    const avg = document.getElementById('m-baseline-avg')!;
    avg.style.display = '';
    avg.classList.add('visible');

    // Populate compiled baseline card
    document.getElementById('m-compiled-carry')!.textContent = formatted.carry;
    document.getElementById('m-compiled-total')!.textContent = formatted.total;
    document.getElementById('m-compiled-height')!.textContent = formatted.height;
    document.getElementById('m-compiled-spin')!.textContent = formatted.spin || '--';
    document.getElementById('m-compiled-land')!.textContent = formatted.landing + '\u00b0';
    document.getElementById('m-compiled-off')!.textContent = formatted.offline || '--';
    const compiled = document.getElementById('m-baseline-compiled')!;
    compiled.style.display = '';
  }

  /** Show the ELDRICK capture card with swing data from the payload (no baseline table). */
  private showEldrickCaptureCard(): void {
    const payload = this.orchestrator.getEldrickPayload();
    if (!payload) return;

    const sw = payload.swing;
    const spinRate = sw.spinRate ?? 0;
    const spinDisplay = spinRate >= 1000 ? (spinRate / 1000).toFixed(1) + 'k' : String(Math.round(spinRate));

    // Hide the baseline table (not used in ELDRICK flow)
    const baselineTable = document.querySelector('.m-baseline-table') as HTMLElement;
    if (baselineTable) baselineTable.style.display = 'none';
    const avgRow = document.getElementById('m-baseline-avg');
    if (avgRow) avgRow.style.display = 'none';

    // Populate compiled card with ELDRICK swing data
    // carry/total will be updated when the baseline shot lands
    const formatted = this.lastBaselineFormatted;
    document.getElementById('m-compiled-carry')!.textContent = formatted?.carry || '--';
    document.getElementById('m-compiled-total')!.textContent = formatted?.total || '--';
    document.getElementById('m-compiled-height')!.textContent = formatted?.height || '--';
    document.getElementById('m-compiled-spin')!.textContent = spinDisplay;
    document.getElementById('m-compiled-land')!.textContent = formatted?.landing ? formatted.landing + '\u00b0' : '--';
    document.getElementById('m-compiled-off')!.textContent = formatted?.offline || '0.0';

    // Update the compiled card label
    const compiledLabel = document.querySelector('#m-baseline-compiled .m-compiled-label') as HTMLElement;
    if (compiledLabel) {
      compiledLabel.textContent = 'SIMULATED FROM YOUR FITTING';
      if (payload.meta.fittingId) {
        compiledLabel.textContent += ` \u00b7 ${payload.meta.fittingId}`;
      }
    }

    // Hide the rehit button (not applicable in ELDRICK flow)
    const rehitBtn = document.getElementById('m-rehit-btn');
    if (rehitBtn) rehitBtn.style.display = 'none';

    const compiled = document.getElementById('m-baseline-compiled')!;
    compiled.style.display = '';
  }

  // ── Mobile Ball Testing Cards ────────────────────────────────────────

  /** Build skeleton for ball phase — leaderboard only during flight. */
  private buildMobileBallSkeleton(): void {
    const container = document.getElementById('m-ball-cards')!;
    container.innerHTML = '';
    container.style.display = 'none';
    // Show leaderboard immediately with placeholder
    this.ballLeaderboard = [];
    this.ballConfigsCompleted = 0;
    const ballLb = document.getElementById('m-ball-leaderboard');
    if (ballLb) {
      ballLb.style.display = '';
      ballLb.dataset.landed = '';
      const entries = document.getElementById('m-ball-lb-entries');
      if (entries) entries.innerHTML = '<div class="m-lb-waiting">Waiting for shots to land\u2026</div>';
    }
    const ballTableWrap = document.getElementById('m-ball-table-wrap');
    if (ballTableWrap) ballTableWrap.style.display = 'none';
    // Clear any previous selection detail
    const prevDetail = document.getElementById('m-ball-selected-detail');
    if (prevDetail) prevDetail.remove();

    // Show selected gear banner
    if (this.selectedGearEntry) {
      const banner = document.createElement('div');
      banner.className = 'm-selected-spec-banner';
      banner.innerHTML = `
        <span class="m-ssb-label">TESTING WITH</span>
        <span class="m-ssb-name">${this.selectedGearEntry.label}</span>
        <span class="m-ssb-detail">${this.selectedGearEntry.specSummary} · ${this.selectedGearEntry.formatted.carry} carry</span>
      `;
      container.appendChild(banner);
    }
  }

  /** Update a single ball card when its shot completes. */
  private updateMobileBallCard(index: number, entry: ComparisonEntry): void {
    // Accumulate for final leaderboard
    this.ballLeaderboard.push({ entry, shotIndex: index });
  }

  /** Finalize ball cards: sort, rank, wire tap-to-select. */
  private finalizeMobileBallCards(results: ComparisonEntry[]): void {
    // Render the final sorted leaderboard once
    this.updateLeaderboard('ball', this.ballLeaderboard);

    // Clear the cards container — table is the only selection UI
    const container = document.getElementById('m-ball-cards')!;
    container.innerHTML = '';

    const baseEntry = results[0];

    const hcp = this.currentHandicap;
    const sorted = results.slice(1).sort((a, b) =>
      fittingScore(b, baseEntry, hcp, this.orchestrator.fittingObjective) - fittingScore(a, baseEntry, hcp, this.orchestrator.fittingObjective));

    // Build ball results table
    this.buildMobileBallTable(sorted, baseEntry);
    this.presentResultChoices('ball', sorted, baseEntry, bestFittingEntry(sorted, baseEntry, hcp, this.orchestrator.fittingObjective));
  }

  /** Build compact ball results table with click selection. */
  private buildMobileBallTable(sorted: ComparisonEntry[], baseEntry: ComparisonEntry): void {
    const tbody = document.getElementById('m-ball-table-rows')!;
    tbody.innerHTML = '';
    const baseCarry = parseFloat(baseEntry.formatted.carry) || 0;
    const baseTotal = parseFloat(baseEntry.formatted.total) || 0;

    for (let i = 0; i < sorted.length; i++) {
      const e = sorted[i];
      const carry = parseFloat(e.formatted.carry) || 0;
      const total = parseFloat(e.formatted.total) || 0;
      const dc = carry - baseCarry;
      const dt = total - baseTotal;
      const spinDisplay = e.spinRate >= 1000 ? (e.spinRate / 1000).toFixed(1) + 'k' : String(Math.round(e.spinRate));

      const tr = document.createElement('tr');
      if (i < 3) tr.classList.add(`m-table-rank-${i + 1}`);
      if (i === 0) tr.classList.add('m-avg-row');
      const spreadText = e.spread ? `${e.spread.carrySpread.toFixed(1)} wide` : '';

      // Inline "why" tag for top 5 ball entries
      let ballWhyTag = '';
      if (i < 5) {
        const whyText = generateShortWhy(e, baseEntry);
        if (whyText) {
          const cls = dt > 0.5 ? 'positive' : dt < -0.5 ? 'negative' : 'neutral';
          ballWhyTag = `<div class="m-why-tag ${cls}">${whyText}</div>`;
        }
      }

      tr.innerHTML = `
        <td>${i + 1}</td>
        <td>${e.label}${ballWhyTag}</td>
        <td>${e.specSummary}</td>
        <td>${e.formatted.carry}${dc !== 0 ? ` <span class="m-card-delta ${dc > 0 ? 'pos' : 'neg'}">${dc > 0 ? '+' : ''}${dc.toFixed(1)}</span>` : ''}</td>
        <td>${e.formatted.total}${dt !== 0 ? ` <span class="m-card-delta ${dt > 0 ? 'pos' : 'neg'}">${dt > 0 ? '+' : ''}${dt.toFixed(1)}</span>` : ''}</td>
        <td>${spinDisplay}</td>
        <td>${e.formatted.offline || '--'}</td>
        <td class="m-table-spread">${spreadText}</td>
      `;

      // Wire click to select this ball
      tr.style.cursor = 'pointer';
      tr.addEventListener('click', () => {
        tbody.querySelectorAll('tr').forEach(r => r.classList.remove('m-table-selected'));
        tr.classList.add('m-table-selected');
        this.selectedBallEntry = e;
        this.displayedBallTotal = parseFloat(e.formatted.total) || 0;
        const originalIndex = this.ballLeaderboard.find(item => item.entry === e)?.shotIndex;
        if (originalIndex !== undefined) this.orchestrator.highlightShot(originalIndex);
        this.showSelectedDetail('ball', e);
        this.setSelectionCta('ball', e);
        trackEvent('ball_selected', {
          label: e.label || '',
          total_distance: this.displayedBallTotal,
          delta: this.displayedBallTotal - this.displayedGearTotal,
          selection_source: this.autoSelectingRecommendation ? 'recommendation' : 'user',
        });
      });

      tbody.appendChild(tr);
    }

    const wrap = document.getElementById('m-ball-table-wrap')!;
    wrap.style.display = '';
  }

  /** Build the summary comparison view — hero gain + before/after. */
  private buildSummaryView(): void {
    const data = this.orchestrator.getSummaryData();
    if (!data) return;
    const summary = buildFittingSummary(data);
    const totalGainForEvent = summary.totalGain;
    trackEvent('fitting_complete', {
      flow: this.orchestrator.isEldrickFlow ? 'eldrick' : 'standard',
      baseline_total: summary.baselineTotal,
      final_total: summary.finalTotal,
      total_gain: totalGainForEvent,
      spec_label: this.selectedSpecEntry?.label || '',
      gear_label: this.selectedGearEntry?.label || '',
      ball_label: this.selectedBallEntry?.label || '',
    });
    const { baseline, bestSpec, bestGear } = data;

    const spinFmt = (spin: number) => spin >= 1000 ? (spin / 1000).toFixed(1) + 'k' : String(Math.round(spin));

    // Use the exact displayed totals the user saw at each step.
    // This guarantees deltas match the numbers shown throughout the experience.
    const { totalGain, specGain, gearGain, ballGain } = summary;
    document.getElementById('m-gain-total')!.textContent = (totalGain >= 0 ? '+' : '') + totalGain.toFixed(1) + ' yds';

    // Incremental gain breakdown: what each stage added

    const stepsEl = document.getElementById('m-sum-steps');
    if (stepsEl) {
      const fmtGain = (v: number) => (v >= 0 ? '+' : '') + v.toFixed(1);
      const specStepLabel = this.orchestrator.isEldrickFlow ? 'Your Core Specs · Simulated' : 'Spec Optimization';
      stepsEl.innerHTML = `
        <div class="m-sum-step">
          <span class="m-sum-step-label">${specStepLabel}</span>
          <span class="m-sum-step-val ${specGain >= 0 ? 'pos' : 'neg'}">${fmtGain(specGain)} yds</span>
        </div>
        <div class="m-sum-step">
          <span class="m-sum-step-label">Gear Upgrade</span>
          <span class="m-sum-step-val ${gearGain >= 0 ? 'pos' : 'neg'}">${fmtGain(gearGain)} yds</span>
        </div>
        ${data.bestBall ? `<div class="m-sum-step">
          <span class="m-sum-step-label">Ball Selection</span>
          <span class="m-sum-step-val ${ballGain >= 0 ? 'pos' : 'neg'}">${fmtGain(ballGain)} yds</span>
        </div>` : ''}
      `;
    }

    // Recommended setup card — include selected ball
    const bestBall = data.bestBall;
    const ballName = bestBall ? bestBall.label : 'Ball not compared';
    document.getElementById('m-sum-rec-name')!.textContent = bestGear.label;
    document.getElementById('m-sum-rec-detail')!.textContent = `${bestGear.specSummary} · ${ballName}`;

    // Fitting breakdown — specs + gear + ball with per-step deltas
    const fmtDelta = (v: number) => (v >= 0 ? '+' : '') + v.toFixed(1) + ' yds';
    const setDelta = (id: string, val: number) => {
      const el = document.getElementById(id);
      if (el) {
        el.textContent = fmtDelta(val);
        el.className = 'm-sum-fitting-delta' + (val < 0 ? ' neg' : '');
      }
    };

    document.getElementById('m-sum-spec-name')!.textContent = bestSpec.label;
    setDelta('m-sum-spec-delta', specGain);
    const sumSpecEl = document.getElementById('m-sum-spec-summary')!;
    if (bestSpec.specs) {
      sumSpecEl.innerHTML = buildSpecDetail(bestSpec.specs);
    } else {
      sumSpecEl.textContent = bestSpec.specSummary;
    }
    document.getElementById('m-sum-gear-name')!.textContent = bestGear.label;
    document.getElementById('m-sum-gear-summary')!.textContent = bestGear.specSummary;
    setDelta('m-sum-gear-delta', gearGain);

    // Ball
    if (bestBall) {
      document.getElementById('m-sum-ball-name')!.textContent = bestBall.label;
      document.getElementById('m-sum-ball-summary')!.textContent = bestBall.specSummary;
      setDelta('m-sum-ball-delta', ballGain);
    } else {
      document.getElementById('m-sum-ball-name')!.textContent = 'Not compared';
      document.getElementById('m-sum-ball-summary')!.textContent = 'Equipment-stage result';
      setDelta('m-sum-ball-delta', 0);
    }

    // Before (baseline)
    if (this.orchestrator.isEldrickFlow) {
      const fittingId = this.orchestrator.getEldrickPayload()?.meta.fittingId;
      document.getElementById('m-sum-base-detail')!.textContent = fittingId
        ? `ELDRICK Capture \u00B7 ${fittingId}`
        : 'ELDRICK Capture';
    } else {
      document.getElementById('m-sum-base-detail')!.textContent = `${this.getClubName()} \u00B7 Stock`;
    }
    document.getElementById('m-sum-base-total')!.textContent = summary.baselineTotal.toFixed(1);
    document.getElementById('m-sum-base-carry')!.textContent = baseline.formatted.carry;
    document.getElementById('m-sum-base-height')!.textContent = baseline.formatted.height;
    document.getElementById('m-sum-base-spin')!.textContent = spinFmt(baseline.spinRate);

    // After (best gear + ball)
    const afterDetail = bestBall ? `${bestGear.specSummary} · ${bestBall.label}` : bestGear.specSummary;
    document.getElementById('m-sum-gear-detail')!.textContent = afterDetail;
    document.getElementById('m-sum-gear-total')!.textContent = summary.finalTotal.toFixed(1);
    document.getElementById('m-sum-gear-carry')!.textContent = summary.final.formatted.carry;
    document.getElementById('m-sum-gear-height')!.textContent = summary.final.formatted.height;
    document.getElementById('m-sum-gear-spin')!.textContent = spinFmt(summary.final.spinRate);

    // Hidden compat elements
    const specCarry = document.getElementById('m-sum-spec-carry');
    if (specCarry) specCarry.textContent = bestSpec.formatted.carry;

    // ── Fitting Recommendation ──────────────────────────────────────────────
    const rawHcpRec = parseFloat((document.getElementById('handicap') as HTMLInputElement).value);
    const handicapRec = isNaN(rawHcpRec) ? 15 : rawHcpRec;
    const selectedSpecs = this.orchestrator.getSelectedSpecs();
    const gearBags = this.orchestrator.getGearBags();

    // Find the gear bag that matches the best gear entry by label
    const matchedBag = gearBags.find(g => g.label === bestGear.label);

    if (selectedSpecs) {
      const rec = generateFittingRecommendation(
        handicapRec,
        this.orchestrator.getClubKey(),
        baseline,
        bestSpec,
        bestGear,
        selectedSpecs,
        matchedBag?.bag ?? null,
        bestBall,
      );
      this.populateRecommendation(rec);
    }

    // Wire action buttons
    document.getElementById('m-action-buy')!.onclick = () => {
      const form = document.getElementById('m-buy-form')!;
      const buyBtn = document.getElementById('m-action-buy')!;
      form.style.display = '';
      buyBtn.style.display = 'none';
      // Focus email input
      setTimeout(() => (document.getElementById('m-buy-email') as HTMLInputElement).focus(), 100);
      trackEvent('buy_click');
    };

    // Email submit handler
    document.getElementById('m-buy-submit')!.onclick = async () => {
      const emailInput = document.getElementById('m-buy-email') as HTMLInputElement;
      const submitBtn = document.getElementById('m-buy-submit') as HTMLButtonElement;
      const successEl = document.getElementById('m-buy-success')!;
      const inputRow = document.querySelector('.m-buy-form-input-row') as HTMLElement;

      const email = emailInput.value.trim();
      if (!email || !email.includes('@') || !email.includes('.')) {
        emailInput.style.borderColor = '#f05450';
        emailInput.focus();
        return;
      }

      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending\u2026';

      try {
        // Send to server
        await fetch('/api/buy-interest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            fitting: {
              specs: this.selectedSpecEntry?.label || '',
              gear: this.selectedGearEntry?.label || '',
              ball: this.selectedBallEntry?.label || '',
              baseline: this.displayedBaselineTotal,
              specTotal: this.displayedSpecTotal,
              gearTotal: this.displayedGearTotal,
              ballTotal: this.displayedBallTotal,
            },
            timestamp: new Date().toISOString(),
          }),
        });
      } catch {
        // Still show success — we'll add a proper endpoint later
      }

      trackEvent('buy_email_submit', { email });

      // Show success
      inputRow.style.display = 'none';
      successEl.style.display = '';
    };
    document.getElementById('m-action-club')!.onclick = () => this.resetTest();

    // Auto-save session for persistence across page refreshes
    const rawHcp = parseFloat((document.getElementById('handicap') as HTMLInputElement).value);
    const handicap = isNaN(rawHcp) ? 15 : rawHcp;
    const session: FittingSession = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      timestamp: Date.now(),
      handicap,
      clubKey: this.clubSelect.value,
      baselineFormatted: { ...baseline.formatted },
      bestSpecLabel: bestSpec.label,
      bestSpecFormatted: { ...bestSpec.formatted },
      bestGearLabel: bestGear.label,
      bestGearFormatted: { ...bestGear.formatted },
      distanceGain: totalGain,
    };
    SessionStore.save(session);
  }

  /** Populate the fitting recommendation section in the summary view. */
  private populateRecommendation(rec: ReturnType<typeof generateFittingRecommendation>): void {
    // Headline
    const headlineEl = document.getElementById('m-rec-headline');
    if (headlineEl) headlineEl.textContent = rec.headline;

    // Recommended setup label
    const setupEl = document.getElementById('m-rec-setup-label');
    if (setupEl) setupEl.textContent = rec.setupLabel;

    // Spec breakdown bullets
    const specList = document.getElementById('m-rec-spec-list');
    if (specList) {
      specList.innerHTML = '';
      for (const line of rec.specBreakdown) {
        const li = document.createElement('li');
        li.textContent = line;
        specList.appendChild(li);
      }
    }

    // Gear breakdown bullets
    const gearList = document.getElementById('m-rec-gear-list');
    if (gearList) {
      gearList.innerHTML = '';
      for (const line of rec.gearBreakdown) {
        const li = document.createElement('li');
        li.textContent = line;
        gearList.appendChild(li);
      }
    }

    // Spin improvement
    const spinEl = document.getElementById('m-rec-spin');
    if (spinEl) spinEl.textContent = rec.spinImprovement;

    // Accuracy gain
    const accEl = document.getElementById('m-rec-accuracy');
    if (accEl) {
      accEl.textContent = rec.accuracyGain > 0
        ? `Simulated lateral finish ${rec.accuracyGain.toFixed(1)} yards closer to center`
        : rec.accuracyGain < -0.5
          ? `Offline moved ${Math.abs(rec.accuracyGain).toFixed(1)} yards \u2014 trade-off for added distance`
          : 'Similar simulated lateral finish';
    }

    // Player insight
    const insightEl = document.getElementById('m-rec-insight');
    if (insightEl) insightEl.textContent = rec.playerInsight;

    // Confidence note
    const confEl = document.getElementById('m-rec-confidence');
    if (confEl) confEl.textContent = rec.confidenceNote;

    // Show the recommendation section
    const recSection = document.getElementById('m-rec-section');
    if (recSection) recSection.style.display = '';
  }

  private updateFittingPace(): void {
    const phase = this.orchestrator.phase;
    this.setPlaybackSpeed(fittingTimeScale(phase, this.fastResults));
    const control = document.getElementById('m-pace-control')!;
    control.hidden = !isComparisonPhase(phase);
    const button = document.getElementById('m-pace-toggle')!;
    button.textContent = this.fastResults ? 'Watch at 1×' : 'Get results faster';
    button.setAttribute('aria-label', this.fastResults ? 'Switch to real-time shot playback' : 'Evaluate at five times speed with the same physics steps');
    document.getElementById('m-pace-label')!.textContent = this.fastResults ? 'Fast results · 5× playback' : 'Live flight · 1× playback';
  }

  /** Populate mobile shot stats (no-op in new 7-step flow — stat elements removed). */
  private showMobileShotData(_formatted: Record<string, string>): void {
    // Stat tiles removed in the new flow; baseline data shown via table rows.
  }

  /** Bind mobile CTA buttons and form toggles. */
  private bindMobileEvents(): void {
    document.getElementById('m-pace-toggle')!.addEventListener('click', () => {
      this.fastResults = !this.fastResults;
      this.updateFittingPace();
    });
    // Single CTA — action depends on current phase + setup step
    this.mobileCta.addEventListener('click', () => {
      const phase = this.orchestrator.phase;
      switch (phase) {
        case TestPhase.Ready:
          if (this.mobileSetupStep === 'club') {
            // Step 1 → Step 2: switch to swing data form
            this.mobileSetupStep = 'swing';
            this.showSheetContent('swing');
            this.setSheetHeader('SWING PROFILE');
            this.setMobileCta('SET MY BASELINE', false, true);
            this.showRandomBtn(false);
            this.applySpeedBracket('moderate');
            this.syncDesktopToMobile();
          } else {
            // Step 2 → Step 3: skip single shot, start multi-baseline directly
            const clubKey = this.clubSelect.value;
            const rawHcp = parseFloat((document.getElementById('handicap') as HTMLInputElement).value);
            const handicap = isNaN(rawHcp) ? 15 : rawHcp;
            const clubSpeed = parseFloat((document.getElementById('clubSpeed') as HTMLInputElement).value);
            const ballSpeed = parseFloat((document.getElementById('ballSpeed') as HTMLInputElement).value);
            const launchAngle = parseFloat((document.getElementById('launchAngle') as HTMLInputElement).value);
            const spinRate = parseFloat((document.getElementById('spinRate') as HTMLInputElement).value);
            const spinAxis = parseFloat((document.getElementById('spinAxis') as HTMLInputElement).value);
            const attackAngle = parseFloat((document.getElementById('attackAngle') as HTMLInputElement).value);
            const clubPath = parseFloat((document.getElementById('clubPath') as HTMLInputElement).value);
            const faceAngle = parseFloat((document.getElementById('faceAngle') as HTMLInputElement).value);
            const launchDirection = deriveLaunchDirection(faceAngle, clubPath, launchAngle, attackAngle);
            const params = createSwingParams(clubSpeed, ballSpeed, launchAngle, launchDirection, spinRate, spinAxis, attackAngle, clubPath, faceAngle, 'medium', 'medium');
            const actualBaseline = { ...this.userBaseline[clubKey], clubSpeed, ballSpeed, launchAngle, spinRate, spinAxis, attackAngle, clubPath, faceAngle };
            // Record baseline in orchestrator then immediately fire multi-baseline shots
            trackEvent('session_start', { flow: 'standard', club: clubKey, club_speed: clubSpeed, handicap });
            this.orchestrator.startBaseline(clubKey, actualBaseline);
            this.orchestrator.startMultiBaseline(clubKey, actualBaseline, params, handicap);
          }
          break;
        case TestPhase.BaselineDone:
          this.orchestrator.proceedToComparing();
          break;
        case TestPhase.Results:
          if (this.selectedSpecEntry) {
            trackEvent('phase_start', { phase: 'gear_test', spec: this.selectedSpecEntry.label || '' });
            this.orchestrator.startGearTest(this.selectedSpecEntry);
          }
          break;
        case TestPhase.GearResults:
          if (this.selectedGearEntry) {
            trackEvent('phase_start', { phase: 'ball_test', gear: this.selectedGearEntry.label || '' });
            this.orchestrator.startBallTest(this.selectedGearEntry);
          }
          break;
        case TestPhase.BallResults:
          if (this.selectedBallEntry) {
            this.orchestrator.transitionToSummary(this.selectedSpecEntry!, this.selectedGearEntry!, this.selectedBallEntry);
          }
          break;
      }
    });

    // Re-hit baseline button
    document.getElementById('m-rehit-btn')!.addEventListener('click', () => {
      this.rehitBaseline();
    });

    // Randomize button
    document.getElementById('m-random-btn')!.addEventListener('click', () => {
      this.onRandomize();
      this.syncDesktopToMobile();
    });

    // Speed bracket selector
    const bracketGrid = document.getElementById('m-speed-brackets');
    if (bracketGrid) {
      bracketGrid.querySelectorAll('.m-speed-card').forEach(btn => {
        btn.addEventListener('click', () => {
          bracketGrid.querySelectorAll('.m-speed-card').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          const bracket = (btn as HTMLElement).dataset.bracket;
          this.applySpeedBracket(bracket || 'moderate');
          this.syncDesktopToMobile();
        });
      });
    }

    // Mobile club select sync (hidden native select)
    document.getElementById('m-club-select')!.addEventListener('change', (e) => {
      const val = (e.target as HTMLSelectElement).value;
      this.clubSelect.value = val;
      this.onClubChange();
      this.syncDesktopToMobile();
    });

    // Club grid button handler
    const clubGrid = document.getElementById('m-club-grid');
    if (clubGrid) {
      clubGrid.querySelectorAll('.m-club-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const club = (btn as HTMLElement).dataset.club;
          if (!club) return;
          clubGrid.querySelectorAll('.m-club-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          // Sync to hidden select + desktop
          const sel = document.getElementById('m-club-select') as HTMLSelectElement;
          sel.value = club;
          sel.dispatchEvent(new Event('change'));
        });
      });
    }

    // Collapsible sections
    this.mobileRoot.querySelectorAll('h4.m-collapsible').forEach(h4 => {
      h4.addEventListener('click', () => {
        h4.parentElement?.classList.toggle('m-collapsed');
      });
    });

    // Sheet handle — toggle between mini/half when in mini state
    document.getElementById('m-sheet-handle')!.addEventListener('click', () => {
      if (this.mobileSheetState === 'mini') {
        this.setSheetState('half');
      } else if (this.mobileSheetState === 'half' && this.mobileCurrentContent !== 'club' && this.mobileCurrentContent !== 'swing') {
        this.setSheetState('mini');
      }
    });

    // Camera angle tabs
    document.querySelectorAll('#mobile-camera-tabs .cam-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelector('[data-review="landings"]')?.setAttribute('aria-pressed', 'false');
        const points = this.orchestrator.getFlightFramingPoints();
        if (this.mobileSheetState === 'flight' || [TestPhase.Results, TestPhase.GearResults, TestPhase.BallResults].includes(this.orchestrator.phase)) {
          this.setSheetState('peek');
        }
        if (points.length) this.camera.setFlightPoints(points);
        if (btn.getAttribute('data-cam') === 'fit') {
          this.orchestrator.highlightShot(-1);
          this.camera.fitFlights();
          return;
        }
        document.querySelectorAll('#mobile-camera-tabs .cam-tab').forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
        const cam = btn.getAttribute('data-cam');
        if (cam === 'behind') {
          this.camera.setMobileDownrangeView();
        } else if (cam === 'side') {
          this.camera.setMobileBaselineView();
        } else if (cam === 'top') {
          this.camera.setMobileTopView();
        }
      });
    });
  }

  /** Sync mobile m-* inputs → desktop inputs so existing logic works. */
  private bindMobileSyncInputs(): void {
    const pairs = [
      ['m-spec-loft', 'spec-loft'],
      ['m-spec-shaft-weight', 'spec-shaft-weight'],
      ['m-spec-shaft-flex', 'spec-shaft-flex'],
      ['m-spec-compression', 'spec-compression'],
      ['m-spec-ball-cover', 'spec-ball-cover'],
      ['m-spec-iron-cat', 'spec-iron-cat'],
      ['m-clubSpeed', 'clubSpeed'],
      ['m-ballSpeed', 'ballSpeed'],
      ['m-launchAngle', 'launchAngle'],
      ['m-launchDir', 'launchDir'],
      ['m-spinRate', 'spinRate'],
      ['m-spinAxis', 'spinAxis'],
      ['m-attackAngle', 'attackAngle'],
      ['m-clubPath', 'clubPath'],
      ['m-faceAngle', 'faceAngle'],
      ['m-handicap', 'handicap'],
      ['m-playerHeight', 'playerHeight'],
      ['m-wristToFloor', 'wristToFloor'],
      ['m-playerWeight', 'playerWeight'],
      ['m-handedness', 'handedness'],
    ];

    for (const [mId, dId] of pairs) {
      const mEl = document.getElementById(mId) as HTMLInputElement | HTMLSelectElement | null;
      const dEl = document.getElementById(dId) as HTMLInputElement | HTMLSelectElement | null;
      if (!mEl || !dEl) continue;

      const handler = () => {
        dEl.value = mEl.value;
        dEl.dispatchEvent(new Event('input', { bubbles: true }));
        dEl.dispatchEvent(new Event('change', { bubbles: true }));
      };
      mEl.addEventListener('input', handler);
      mEl.addEventListener('change', handler);
    }
  }

  /** Copy desktop input values → mobile inputs (after randomize or club change). */
  private syncDesktopToMobile(): void {
    const pairs: [string, string][] = [
      ['spec-loft', 'm-spec-loft'],
      ['spec-shaft-weight', 'm-spec-shaft-weight'],
      ['spec-shaft-flex', 'm-spec-shaft-flex'],
      ['spec-compression', 'm-spec-compression'],
      ['spec-ball-cover', 'm-spec-ball-cover'],
      ['spec-iron-cat', 'm-spec-iron-cat'],
      ['clubSpeed', 'm-clubSpeed'],
      ['ballSpeed', 'm-ballSpeed'],
      ['launchAngle', 'm-launchAngle'],
      ['launchDir', 'm-launchDir'],
      ['spinRate', 'm-spinRate'],
      ['spinAxis', 'm-spinAxis'],
      ['attackAngle', 'm-attackAngle'],
      ['clubPath', 'm-clubPath'],
      ['faceAngle', 'm-faceAngle'],
      ['handicap', 'm-handicap'],
      ['club-select', 'm-club-select'],
    ];
    for (const [dId, mId] of pairs) {
      const dEl = document.getElementById(dId) as HTMLInputElement | HTMLSelectElement | null;
      const mEl = document.getElementById(mId) as HTMLInputElement | HTMLSelectElement | null;
      if (dEl && mEl) mEl.value = dEl.value;
    }
  }

  // ── Mobile Spec Cards ──────────────────────────────────────────────────

  /** Build skeleton for specs phase — leaderboard only during flight. */
  private buildMobileSpecsSkeleton(): void {
    const container = document.getElementById('m-specs-cards')!;
    container.innerHTML = '';
    container.style.display = 'none'; // Hide cards — leaderboard is the in-flight view
    // Reset leaderboard + table — show leaderboard immediately with placeholder
    this.specLeaderboard = [];
    const specLb = document.getElementById('m-spec-leaderboard');
    if (specLb) {
      specLb.style.display = '';
      specLb.dataset.landed = '';
      const entries = document.getElementById('m-spec-lb-entries');
      if (entries) entries.innerHTML = '<div class="m-lb-waiting">Waiting for shots to land\u2026</div>';
    }
    const specTable = document.getElementById('m-spec-table-wrap');
    if (specTable) specTable.style.display = 'none';
    // Clear any previous selection detail
    const prevDetail = document.getElementById('m-spec-selected-detail');
    if (prevDetail) prevDetail.remove();

    // Baseline card
    const baseEntry = this.orchestrator.getBaselineEntry();
    if (baseEntry) {
      const card = this.buildMobileCard(baseEntry, -1, true);
      card.classList.add('baseline-card');
      container.appendChild(card);
    }

    // 10 skeleton cards
    const meta = this.orchestrator.getConfigMeta();
    for (let i = 0; i < meta.length; i++) {
      const card = document.createElement('div');
      card.className = 'm-result-card skeleton';
      card.id = `m-spec-card-${i}`;
      card.innerHTML = `
        <div class="m-card-color-strip" style="background:${meta[i].color.hex}"></div>
        <div class="m-card-body">
          <div class="m-card-header"><span class="m-card-title">${meta[i].label}</span></div>
          <div class="m-card-spec">${meta[i].specSummary}</div>
          <div class="m-card-stats"><span>--</span></div>
          <div class="m-card-secondary"><span>Loading\u2026</span></div>
        </div>
      `;
      container.appendChild(card);
    }
  }

  /** Update a single spec card when its shot completes. */
  private updateMobileSpecCard(index: number, entry: ComparisonEntry): void {
    const card = document.getElementById(`m-spec-card-${index}`);
    if (!card) return;

    const baseEntry = this.orchestrator.getBaselineEntry();
    const baseCarry = parseFloat(baseEntry?.formatted.carry || '0');
    const baseTotal = parseFloat(baseEntry?.formatted.total || '0');
    const carry = parseFloat(entry.formatted.carry) || 0;
    const total = parseFloat(entry.formatted.total) || 0;
    const dc = carry - baseCarry;
    const dt = total - baseTotal;

    const carryDelta = dc !== 0 ? `<span class="m-card-delta ${dc > 0 ? 'pos' : 'neg'}">${dc > 0 ? '+' : ''}${dc.toFixed(1)}</span>` : '';
    const totalDelta = dt !== 0 ? `<span class="m-card-delta ${dt > 0 ? 'pos' : 'neg'}">${dt > 0 ? '+' : ''}${dt.toFixed(1)}</span>` : '';

    const spinDisplay = entry.spinRate >= 1000
      ? (entry.spinRate / 1000).toFixed(1) + 'k'
      : String(Math.round(entry.spinRate));

    const spreadLine = entry.spread
      ? `<div class="m-card-spread">Middle 80%: ${entry.spread.carrySpread.toFixed(1)}yd carry width · ${entry.spread.offlineSpread.toFixed(1)}yd landing width</div>`
      : '';

    card.classList.remove('skeleton');
    card.querySelector('.m-card-body')!.innerHTML = `
      <div class="m-card-header"><span class="m-card-title">${entry.label}</span></div>
      <div class="m-card-spec">${entry.specSummary}</div>
      <div class="m-card-stats">
        <div><div class="m-card-stat-label">Carry</div><span class="m-card-stat-primary">${entry.formatted.carry}<span class="m-card-stat-unit">yd</span></span>${carryDelta}</div>
        <div><div class="m-card-stat-label">Total</div><span class="m-card-stat-primary">${entry.formatted.total}<span class="m-card-stat-unit">yd</span></span>${totalDelta}</div>
      </div>
      <div class="m-card-secondary">
        <span>Ht ${entry.formatted.height}</span>
        <span>Land ${entry.formatted.landing}\u00B0</span>
        <span>Spin ${spinDisplay}</span>
        <span>Off ${entry.formatted.offline || '--'}</span>
      </div>
      ${spreadLine}
    `;

    // Accumulate for final leaderboard (rendered once all shots land)
    this.specLeaderboard.push({ entry, shotIndex: index });
  }

  /** Finalize spec cards: sort, rank, wire tap-to-select. Top 2 featured, rest collapsible. */
  private finalizeMobileSpecsCards(results: ComparisonEntry[]): void {
    // Render the final sorted leaderboard once (no more re-sorts)
    this.updateLeaderboard('spec', this.specLeaderboard);

    // Clear the cards container — table is the only selection UI now
    const container = document.getElementById('m-specs-cards')!;
    container.innerHTML = '';

    const baseEntry = results[0];
    const hcp = this.currentHandicap;

    // Rank modeled carry/miss tradeoffs; keep Core's recommendation authoritative.
    const sorted = results.slice(1).sort((a, b) =>
      fittingScore(b, baseEntry, hcp, this.orchestrator.fittingObjective) - fittingScore(a, baseEntry, hcp, this.orchestrator.fittingObjective)
    );
    sorted.sort((a, b) => Number(b.recommendationSource === 'core') - Number(a.recommendationSource === 'core'));

    // Build spec table as the primary selection mechanism
    this.buildMobileSpecTable(sorted, baseEntry);
    this.presentResultChoices('spec', sorted, baseEntry, bestFittingEntry(sorted, baseEntry, hcp, this.orchestrator.fittingObjective));
  }

  /** Build compact spec results table overview with top-3 highlights + click selection. */
  private buildMobileSpecTable(sorted: ComparisonEntry[], baseEntry: ComparisonEntry): void {
    const tbody = document.getElementById('m-spec-table-rows')!;
    tbody.innerHTML = '';
    const baseCarry = parseFloat(baseEntry.formatted.carry) || 0;
    const baseTotal = parseFloat(baseEntry.formatted.total) || 0;

    // Need results + origMap for selection
    const results = this.orchestrator.getResults?.() || [];
    const origMap = new Map<ComparisonEntry, number>();
    results.forEach((e, i) => origMap.set(e, i));

    for (let i = 0; i < sorted.length; i++) {
      const e = sorted[i];
      const carry = parseFloat(e.formatted.carry) || 0;
      const total = parseFloat(e.formatted.total) || 0;
      const dc = carry - baseCarry;
      const dt = total - baseTotal;
      const spinDisplay = e.spinRate >= 1000 ? (e.spinRate / 1000).toFixed(1) + 'k' : String(Math.round(e.spinRate));

      const tr = document.createElement('tr');
      // Highlight top 3 rows
      if (i < 3) tr.classList.add(`m-table-rank-${i + 1}`);
      if (i === 0) tr.classList.add('m-avg-row');
      const spreadText = e.spread ? `${e.spread.carrySpread.toFixed(1)} wide` : '';

      // Inline "why" tag for top 5 entries
      let whyTag = '';
      if (i < 5) {
        const whyText = generateShortWhy(e, baseEntry);
        if (whyText) {
          const cls = dt > 0.5 ? 'positive' : dt < -0.5 ? 'negative' : 'neutral';
          whyTag = `<div class="m-why-tag ${cls}">${whyText}</div>`;
        }
      }

      // Badge ELDRICK Rec entries
      const isEldrickRec = e.label === 'ELDRICK Rec';
      const labelHtml = isEldrickRec
        ? `<span class="m-eldrick-badge">YOUR FIT</span> ${e.label}`
        : e.label;

      tr.innerHTML = `
        <td>${i + 1}</td>
        <td>${labelHtml}${whyTag}</td>
        <td>${e.formatted.carry}${dc !== 0 ? ` <span class="m-card-delta ${dc > 0 ? 'pos' : 'neg'}">${dc > 0 ? '+' : ''}${dc.toFixed(1)}</span>` : ''}</td>
        <td>${e.formatted.total}${dt !== 0 ? ` <span class="m-card-delta ${dt > 0 ? 'pos' : 'neg'}">${dt > 0 ? '+' : ''}${dt.toFixed(1)}</span>` : ''}</td>
        <td>${e.formatted.height}</td>
        <td>${spinDisplay}</td>
        <td>${e.formatted.landing}\u00B0</td>
        <td>${e.formatted.offline || '--'}</td>
        <td class="m-table-spread">${spreadText}</td>
      `;

      // Wire click to select this config
      tr.style.cursor = 'pointer';
      tr.addEventListener('click', () => {
        tbody.querySelectorAll('tr').forEach(r => r.classList.remove('m-table-selected'));
        tr.classList.add('m-table-selected');
        const origIdx = origMap.get(e) ?? 0;
        this.onSelectConfig(results, origIdx);
        this.orchestrator.highlightShot(e.configIndex ?? -1);
        this.showSelectedDetail('spec', e);
        this.setSelectionCta('spec', e);
      });

      tbody.appendChild(tr);
    }

    const wrap = document.getElementById('m-spec-table-wrap')!;
    wrap.style.display = '';
  }

  /** Show selected config detail panel ABOVE the table. */
  private showSelectedDetail(type: 'spec' | 'gear' | 'ball', entry: ComparisonEntry): void {
    let detail = document.getElementById(`m-${type}-selected-detail`);
    if (!detail) {
      detail = document.createElement('div');
      detail.id = `m-${type}-selected-detail`;
      detail.className = 'm-selected-detail';
      const wrap = document.getElementById(`m-${type}-table-wrap`);
      if (wrap) (wrap.closest('details') || wrap).before(detail);
    }
    const specs = entry.specSummary || '';
    const color = entry.colorHex || '#4ade80';
    const showSpecs = specs && specs !== entry.label;
    const detailSpread = entry.spread
      ? `<div class="m-sd-spread">Middle 80%: ${entry.spread.carrySpread.toFixed(1)}yd carry width · ${entry.spread.offlineSpread.toFixed(1)}yd landing width</div>`
      : '';
    const specGridHtml = (type === 'spec' && entry.specs)
      ? buildSpecDetail(entry.specs)
      : (showSpecs ? `<div class="m-sd-specs">${specs}</div>` : '');

    // Generate contextual "why" explanation vs baseline
    let whyHtml = '';
    const baseEntry = this.orchestrator.getBaselineEntry?.();
    if (baseEntry && entry.label !== 'Baseline') {
      const why = generateWhyExplanation(entry, baseEntry);
      whyHtml = `<div class="m-sd-why">${why}</div>`;
    }

    detail.innerHTML = `
      <div class="m-sd-header">
        <span class="m-sd-color" style="background:${color}"></span>
        <span class="m-sd-name">${entry.label}</span>
      </div>
      ${specGridHtml ? `<details class="m-result-details"><summary>Selected setup details</summary>${specGridHtml}</details>` : ''}
      <div class="m-sd-stats">
        <span>Carry <strong>${entry.formatted.carry}</strong></span>
        <span>Total <strong>${entry.formatted.total}</strong></span>
        <span>Off <strong>${entry.formatted.offline || '0.0'}</strong></span>
      </div>
      ${detailSpread}
      ${whyHtml}
    `;
    detail.style.display = '';
  }

  // ── Mobile Gear Cards ─────────────────────────────────────────────────

  /** Build skeleton for gear phase — leaderboard only during flight. */
  private buildMobileGearSkeleton(): void {
    const container = document.getElementById('m-gear-cards')!;
    container.innerHTML = '';
    container.style.display = 'none'; // Hide cards — leaderboard is the in-flight view
    // Hide gear table, show leaderboard immediately with placeholder
    const gearTableWrap = document.getElementById('m-gear-table-wrap');
    if (gearTableWrap) gearTableWrap.style.display = 'none';
    this.gearLeaderboard = [];
    const gearLb = document.getElementById('m-gear-leaderboard');
    if (gearLb) {
      gearLb.style.display = '';
      gearLb.dataset.landed = '';
      const entries = document.getElementById('m-gear-lb-entries');
      if (entries) entries.innerHTML = '<div class="m-lb-waiting">Waiting for shots to land\u2026</div>';
    }
    // Clear any previous selection detail
    const prevDetail = document.getElementById('m-gear-selected-detail');
    if (prevDetail) prevDetail.remove();

    // Show selected spec banner
    if (this.selectedSpecEntry) {
      const banner = document.createElement('div');
      banner.className = 'm-selected-spec-banner';
      banner.innerHTML = `
        <span class="m-ssb-label">TESTING WITH</span>
        <span class="m-ssb-name">${this.selectedSpecEntry.label}</span>
        <span class="m-ssb-detail">${this.selectedSpecEntry.specSummary} \u00B7 ${this.selectedSpecEntry.formatted.carry} carry</span>
      `;
      container.appendChild(banner);
    }

    // Gear baseline card
    const gearBase = this.orchestrator.getGearBaselineEntry();
    if (gearBase) {
      const card = this.buildMobileCard(gearBase, -1, true);
      card.classList.add('baseline-card');
      container.appendChild(card);
    }

    // 10 skeleton cards
    const meta = this.orchestrator.getGearConfigMeta();
    for (let i = 0; i < meta.length; i++) {
      const card = document.createElement('div');
      card.className = 'm-result-card skeleton';
      card.id = `m-gear-card-${i}`;
      card.innerHTML = `
        <div class="m-card-color-strip" style="background:${meta[i].color.hex}"></div>
        <div class="m-card-body">
          <div class="m-card-header"><span class="m-card-title">${meta[i].label}</span></div>
          <div class="m-card-spec">${meta[i].specSummary}</div>
          <div class="m-card-stats"><span>--</span></div>
          <div class="m-card-secondary"><span>Loading\u2026</span></div>
        </div>
      `;
      container.appendChild(card);
    }
  }

  /** Update a single gear card when its shot completes. */
  private updateMobileGearCard(index: number, entry: ComparisonEntry): void {
    const card = document.getElementById(`m-gear-card-${index}`);
    if (!card) return;

    const baseEntry = this.orchestrator.getGearBaselineEntry();
    const baseCarry = parseFloat(baseEntry?.formatted.carry || '0');
    const baseTotal = parseFloat(baseEntry?.formatted.total || '0');
    const carry = parseFloat(entry.formatted.carry) || 0;
    const total = parseFloat(entry.formatted.total) || 0;
    const dc = carry - baseCarry;
    const dt = total - baseTotal;

    const carryDelta = dc !== 0 ? `<span class="m-card-delta ${dc > 0 ? 'pos' : 'neg'}">${dc > 0 ? '+' : ''}${dc.toFixed(1)}</span>` : '';
    const totalDelta = dt !== 0 ? `<span class="m-card-delta ${dt > 0 ? 'pos' : 'neg'}">${dt > 0 ? '+' : ''}${dt.toFixed(1)}</span>` : '';

    const spinDisplay = entry.spinRate >= 1000
      ? (entry.spinRate / 1000).toFixed(1) + 'k'
      : String(Math.round(entry.spinRate));

    const gearSpreadLine = entry.spread
      ? `<div class="m-card-spread">Middle 80%: ${entry.spread.carrySpread.toFixed(1)}yd carry width · ${entry.spread.offlineSpread.toFixed(1)}yd landing width</div>`
      : '';

    card.classList.remove('skeleton');
    card.querySelector('.m-card-body')!.innerHTML = `
      <div class="m-card-header"><span class="m-card-title">${entry.label}</span></div>
      <div class="m-card-spec">${entry.specSummary}</div>
      <div class="m-card-stats">
        <div><div class="m-card-stat-label">Carry</div><span class="m-card-stat-primary">${entry.formatted.carry}<span class="m-card-stat-unit">yd</span></span>${carryDelta}</div>
        <div><div class="m-card-stat-label">Total</div><span class="m-card-stat-primary">${entry.formatted.total}<span class="m-card-stat-unit">yd</span></span>${totalDelta}</div>
      </div>
      <div class="m-card-secondary">
        <span>Ht ${entry.formatted.height}</span>
        <span>Land ${entry.formatted.landing}\u00B0</span>
        <span>Spin ${spinDisplay}</span>
        <span>Off ${entry.formatted.offline || '--'}</span>
      </div>
      ${gearSpreadLine}
    `;

    // Accumulate for final leaderboard (rendered once all shots land)
    this.gearLeaderboard.push({ entry, shotIndex: index });
  }

  /** Finalize gear cards: sort, rank, wire tap-to-select for tweak. */
  private finalizeMobileGearCards(results: ComparisonEntry[]): void {
    // Render the final sorted leaderboard once (no more re-sorts)
    this.updateLeaderboard('gear', this.gearLeaderboard);

    // Clear the cards container — table is the only selection UI now
    const container = document.getElementById('m-gear-cards')!;
    container.innerHTML = '';

    const baseEntry = results[0];
    const hcp = this.currentHandicap;

    // Sort by modeled carry and miss tradeoffs,
    // then prepend baseline so users can keep specs if gear options underperform.
    const sortedGear = results.slice(1).sort((a, b) =>
      fittingScore(b, baseEntry, hcp, this.orchestrator.fittingObjective) - fittingScore(a, baseEntry, hcp, this.orchestrator.fittingObjective)
    );
    const sorted = [baseEntry, ...sortedGear];

    // Build gear table as the primary selection mechanism
    this.buildMobileGearTable(sorted, baseEntry, results);
    this.presentResultChoices('gear', sorted, baseEntry, bestFittingEntry(sorted, baseEntry, hcp, this.orchestrator.fittingObjective));
  }

  /** One final ranking, readable choices first; technical rows remain available. */
  private presentResultChoices(type: 'spec' | 'gear' | 'ball', entries: ComparisonEntry[], baseline: ComparisonEntry,
    recommended: ComparisonEntry | null): void {
    document.getElementById(`m-${type}-leaderboard`)!.style.display = 'none';
    document.getElementById(`m-${type}-choices`)?.remove();
    const wrap = document.getElementById(`m-${type}-table-wrap`)!;
    let details = wrap.closest('details');
    if (!details) {
      details = document.createElement('details');
      details.className = 'm-result-details m-all-results';
      const title = document.createElement('summary');
      details.append(title);
      wrap.before(details);
      details.append(wrap);
    }
    details.open = false;
    details.querySelector('summary')!.textContent = `Compare all ${entries.length} setups · technical data`;
    const choices = document.createElement('section');
    choices.id = `m-${type}-choices`;
    choices.className = 'm-result-choices';
    const heading = document.createElement('h3');
    const core = recommended?.recommendationSource === 'core';
    const similar = recommended && entries.some(entry => entry !== recommended && similarPerformance(entry, recommended, this.orchestrator.fittingObjective));
    heading.textContent = core ? 'Your Core recommendation' : 'Compare the whole shot pattern';
    const note = document.createElement('p');
    note.textContent = core ? 'Core chose your specs; Lab predicts the shot pattern. Choose another option to override it.'
      : `${similar ? 'Several setups have similar simulated performance. A starting option is selected—not a proven winner.' : 'A simulated starting option is selected.'} Choose another option to override it.`;
    choices.append(heading, note);
    const evidence = document.createElement('details');
    evidence.className = 'm-pattern-evidence';
    const evidenceTitle = document.createElement('summary');
    evidenceTitle.textContent = `How to read this · ${this.orchestrator.fittingObjective} objective`;
    const evidenceText = document.createElement('p');
    evidenceText.textContent = baseline.pattern
      ? `${baseline.pattern.shots.length} identical inputs per setup. ${baseline.pattern.source === 'measured-launches' ? 'Measured launch cohort; all outcomes here are simulated.' : 'Modeled swings, not measured customer dispersion.'} Carry/total are medians. Landing width spans the middle 80%; large miss is the 90th-percentile distance left or right of target. Small samples make these descriptive, not confidence bounds. Local ranking balances carry against large misses and width; spin is not penalized for changing. Within one utility point we call performance similar. Equipment responses and turf roll remain estimates.`
      : 'Pattern evaluation unavailable. These are single-flight estimates, not dispersion or a reliable setup ranking.';
    evidence.append(evidenceTitle, evidenceText);
    const candidates = entries.map((entry, index) => ({ entry, index })).filter(({entry}) => type !== 'gear' || entry !== baseline);
    const options = candidates.slice(0, 3);
    if (type === 'gear') options.push({ entry: baseline, index: 0 });
    const rows = document.querySelectorAll<HTMLTableRowElement>(`#m-${type}-table-rows tr`);
    for (const {entry, index} of options) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'm-result-choice';
      if (entry === recommended) {
        button.classList.add('recommended');
        button.dataset.recommended = 'true';
      }
      button.dataset.row = String(index);
      button.style.setProperty('--choice-color', entry.colorHex || '#4ade80');
      const head = document.createElement('span');
      head.className = 'm-choice-head';
      const title = document.createElement('strong');
      title.textContent = entry === baseline ? 'Keep current setup' : entry.label;
      const action = document.createElement('span');
      action.className = 'm-choice-action';
      action.textContent = entry === recommended ? 'RECOMMENDED' : 'SELECT';
      head.append(title, action);
      const setup = document.createElement('span');
      setup.className = 'm-choice-setup';
      if (type === 'spec') setup.textContent = entry.specSummary;
      const stats = document.createElement('span');
      stats.className = 'm-choice-stats';
      stats.textContent = entry.pattern
        ? `${entry.pattern.typicalCarry.toFixed(1)} yd typical carry · ${entry.pattern.landingWidth.toFixed(1)} yd landing width`
        : `${entry.formatted.carry} carry · ${entry.formatted.total} total · ${entry.formatted.offline || '0.0'} lateral (yd)`;
      const why = document.createElement('span');
      why.className = 'm-choice-why';
      const delta = ((entry.pattern?.typicalCarry ?? Number(entry.formatted.carry))
        - (baseline.pattern?.typicalCarry ?? Number(baseline.formatted.carry))).toFixed(1);
      why.textContent = `${Number(delta) >= 0 ? '+' : ''}${delta} yd carry vs stage current · ${entry.pattern ? `${entry.pattern.badMiss.toFixed(1)} yd large miss` : `${Math.round(entry.spinRate).toLocaleString()} rpm`}`;
      if (!core && recommended && entry !== recommended && similarPerformance(entry, recommended, this.orchestrator.fittingObjective)) {
        why.textContent += ' · Similar performance';
      }
      button.append(head);
      if (setup.textContent) button.append(setup);
      button.append(stats, why);
      button.addEventListener('click', () => rows[index]?.click());
      const selected = rows[index]?.classList.contains('m-table-selected') || false;
      button.classList.toggle('selected', selected);
      button.setAttribute('aria-pressed', String(selected));
      action.textContent = selected ? (entry === recommended ? '★ RECOMMENDED' : '\u2713 SELECTED')
        : (entry === recommended ? 'RECOMMENDED' : 'SELECT');
      choices.append(button);
    }
    choices.append(evidence);
    details.before(choices);
    rows.forEach((row, index) => {
      row.dataset.configIndex = String(entries[index].configIndex ?? -1);
      row.tabIndex = 0;
      row.setAttribute('role', 'button');
      row.setAttribute('aria-label', `Select ${entries[index].label}`);
      row.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); row.click(); }
      });
      row.addEventListener('click', () => {
        this.updateComparisonReview(type, baseline, entries[index]);
        choices.querySelectorAll<HTMLButtonElement>('button').forEach(button => {
          const selected = Number(button.dataset.row) === index;
          button.classList.toggle('selected', selected);
          button.setAttribute('aria-pressed', String(selected));
          const isRecommended = button.dataset.recommended === 'true';
          button.querySelector<HTMLElement>('.m-choice-action')!.textContent = selected
            ? (isRecommended ? '★ RECOMMENDED' : '\u2713 SELECTED')
            : (isRecommended ? 'RECOMMENDED' : 'SELECT');
        });
      });
    });
    const recommendedIndex = recommended ? entries.indexOf(recommended) : -1;
    if (recommendedIndex >= 0) {
      rows[recommendedIndex]?.classList.add('m-table-recommended');
      this.autoSelectingRecommendation = true;
      rows[recommendedIndex]?.click();
      this.autoSelectingRecommendation = false;
      this.setSelectionCta(type, recommended!);
      trackEvent('recommendation_preselected', { stage: type, label: recommended!.label,
        score: Number(fittingScore(recommended!, baseline, this.currentHandicap, this.orchestrator.fittingObjective).toFixed(2)),
        recommendation_source: core ? 'core' : 'simulation', similar_performance: !!similar });
    }
    const sheetBody = document.querySelector<HTMLElement>('.mobile-sheet-body')!;
    sheetBody.scrollTop = 0;
    sheetBody.scrollLeft = 0;
  }

  /** Pinned outside the scrolling results: camera inspection never changes the
   * selected equipment or the continue action. */
  private updateComparisonReview(type: 'spec' | 'gear' | 'ball', current: ComparisonEntry, selected: ComparisonEntry): void {
    document.getElementById('m-comparison-review')?.remove();
    if (!this.orchestrator.showComparison(current, selected)) return;
    const bar = document.createElement('section');
    bar.id = 'm-comparison-review';
    bar.setAttribute('aria-label', 'Compare flights');
    bar.style.setProperty('--selection-color', selected.colorHex);
    const legend = document.createElement('p');
    legend.className = 'm-review-legend';
    const stageLabel = type === 'spec' ? 'Your current' : type === 'gear' ? 'Chosen specs' : 'Chosen gear';
    const currentLabel = document.createElement('span'); currentLabel.textContent = `○ ${stageLabel}`;
    currentLabel.style.color = '#e5edf7';
    const selectionLabel = document.createElement('span'); selectionLabel.textContent = ` · ● ${selected.label}`;
    legend.append(currentLabel, selectionLabel);
    const controls = document.createElement('div');
    controls.className = 'm-review-controls';
    const button = (label: string, name: string, action: () => void) => {
      const b = document.createElement('button'); b.type = 'button'; b.textContent = label;
      b.dataset.review = name; b.addEventListener('click', action); controls.append(b); return b;
    };
    const focus = (which: 'current' | 'selected') => {
      this.orchestrator.focusComparison(which);
      this.orchestrator.highlightShot(which === 'current' ? -1 : selected === current ? -1 : selected.configIndex ?? -1);
      controls.querySelectorAll<HTMLButtonElement>('[data-review="current"], [data-review="selected"]').forEach(b => {
        b.setAttribute('aria-pressed', String(b.dataset.review === which));
      });
    };
    button('View current', 'current', () => focus('current'));
    button(selected.recommendationSource === 'core' ? 'View Core fit' : 'View selection', 'selected', () => focus('selected'));
    button('↻ Replay pair', 'replay', () => {
      this.setSheetState('peek');
      document.querySelector<HTMLButtonElement>('#mobile-camera-tabs [data-cam="behind"]')?.click();
      this.orchestrator.replayComparison();
    });
    const landing = button('Landings', 'landings', () => {
      this.orchestrator.setLandingPatterns(true);
      document.querySelector<HTMLButtonElement>('#mobile-camera-tabs [data-cam="top"]')?.click();
      this.camera.setFlightPoints([current.pattern!, selected.pattern!].flatMap(p => p.shots.map(s => s.result.landingPosition)));
      this.camera.setMobileTopView();
      landing.setAttribute('aria-pressed', 'true');
    });
    landing.setAttribute('aria-pressed', 'false');
    this.orchestrator.setLandingPatterns(true);
    const delta = document.createElement('p');
    delta.className = 'm-review-delta'; delta.setAttribute('aria-live', 'polite');
    const carry = selected.pattern!.typicalCarry - current.pattern!.typicalCarry;
    const width = selected.pattern!.landingWidth - current.pattern!.landingWidth;
    delta.textContent = `${carry >= 0 ? '+' : ''}${carry.toFixed(1)} yd carry · ${Math.abs(width).toFixed(1)} yd ${width <= 0 ? 'narrower' : 'wider'} · simulated`;
    bar.append(legend, controls, delta);
    document.querySelector('.mobile-sheet-body')!.before(bar);
    focus('selected');
  }

  /** Build compact gear results table with tap-to-select + top-3 highlights. */
  private buildMobileGearTable(sorted: ComparisonEntry[], baseEntry: ComparisonEntry, allResults?: ComparisonEntry[]): void {
    const tbody = document.getElementById('m-gear-table-rows')!;
    tbody.innerHTML = '';
    const baseCarry = parseFloat(baseEntry.formatted.carry) || 0;
    const baseTotal = parseFloat(baseEntry.formatted.total) || 0;

    for (let i = 0; i < sorted.length; i++) {
      const e = sorted[i];
      const isBaselineRow = e === baseEntry;
      const carry = parseFloat(e.formatted.carry) || 0;
      const total = parseFloat(e.formatted.total) || 0;
      const dc = carry - baseCarry;
      const dt = total - baseTotal;
      const spinDisplay = e.spinRate >= 1000 ? (e.spinRate / 1000).toFixed(1) + 'k' : String(Math.round(e.spinRate));

      const tr = document.createElement('tr');
      if (isBaselineRow) tr.classList.add('m-avg-row');
      if (!isBaselineRow && i <= 3) tr.classList.add(`m-table-rank-${i}`);
      const gearSpreadText = e.spread ? `${e.spread.carrySpread.toFixed(1)} wide` : '';

      // Inline "why" tag for top 5 gear entries
      let gearWhyTag = '';
      if (!isBaselineRow && i < 6) {
        const whyText = generateShortWhy(e, baseEntry);
        if (whyText) {
          const cls = dt > 0.5 ? 'positive' : dt < -0.5 ? 'negative' : 'neutral';
          gearWhyTag = `<div class="m-why-tag ${cls}">${whyText}</div>`;
        }
      }

      tr.innerHTML = `
        <td>${isBaselineRow ? 'BASE' : i}</td>
        <td>${e.label}${gearWhyTag}</td>
        <td>${e.formatted.carry}${!isBaselineRow && dc !== 0 ? ` <span class="m-card-delta ${dc > 0 ? 'pos' : 'neg'}">${dc > 0 ? '+' : ''}${dc.toFixed(1)}</span>` : ''}</td>
        <td>${e.formatted.total}${!isBaselineRow && dt !== 0 ? ` <span class="m-card-delta ${dt > 0 ? 'pos' : 'neg'}">${dt > 0 ? '+' : ''}${dt.toFixed(1)}</span>` : ''}</td>
        <td>${e.formatted.height}</td>
        <td>${spinDisplay}</td>
        <td>${e.formatted.landing}\u00B0</td>
        <td>${e.formatted.offline || '--'}</td>
        <td class="m-table-spread">${gearSpreadText}</td>
      `;

      // Wire click to select this gear config
      if (allResults) {
        tr.style.cursor = 'pointer';
        tr.addEventListener('click', () => {
          tbody.querySelectorAll('tr').forEach(r => r.classList.remove('m-table-selected'));
          tr.classList.add('m-table-selected');
          this.orchestrator.highlightShot(isBaselineRow ? -1 : e.configIndex ?? -1);
          this.selectedGearEntry = e;
          this.displayedGearTotal = parseFloat(e.formatted.total) || 0;
          this.showSelectedDetail('gear', e);
          this.setSelectionCta('gear', e);
          trackEvent('gear_selected', {
            label: e.label || '',
            total_distance: this.displayedGearTotal,
            delta: this.displayedGearTotal - this.displayedSpecTotal,
            selection_source: this.autoSelectingRecommendation ? 'recommendation' : 'user',
          });
        });
      }

      tbody.appendChild(tr);
    }

    const wrap = document.getElementById('m-gear-table-wrap')!;
    wrap.style.display = '';

    // Keeping the current setup is an explicit choice, not a silent default.
  }

  /** Update the live top-5 leaderboard during testing phases. */
  private updateLeaderboard(type: 'spec' | 'gear' | 'ball', entries: { entry: ComparisonEntry; shotIndex: number }[]): void {
    const container = document.getElementById(`m-${type}-lb-entries`)!;
    const wrapper = document.getElementById(`m-${type}-leaderboard`)!;

    // Mark as landed so in-flight updates stop overwriting
    wrapper.dataset.landed = 'true';

    // Sort by total distance — same metric as in-flight leaderboard so positions
    // never reshuffle. Composite score is used separately in the results table.
    const sorted = [...entries].sort((a, b) => {
      const totalA = parseFloat(a.entry.formatted.total) || 0;
      const totalB = parseFloat(b.entry.formatted.total) || 0;
      return totalB - totalA;
    });
    const top = sorted.slice(0, 15);

    // Track previous labels for shuffle animation
    const prevLabels = Array.from(container.querySelectorAll('.m-lb-name')).map(el => el.textContent);

    container.innerHTML = '';
    for (let i = 0; i < top.length; i++) {
      const { entry: e, shotIndex } = top[i];
      const div = document.createElement('div');
      div.className = `m-lb-entry rank-${i + 1}`;
      div.dataset.shotIndex = String(shotIndex);

      // Shuffle animation if this entry moved positions
      if (prevLabels.length > 0 && prevLabels[i] !== e.label) {
        div.classList.add('m-lb-shuffle');
      }

      const spinDisplay = e.spinRate >= 1000 ? (e.spinRate / 1000).toFixed(1) + 'k spin' : e.spinRate > 0 ? Math.round(e.spinRate) + ' spin' : '';
      const specText = e.specSummary && e.specSummary !== e.label ? e.specSummary : '';
      const specLine = specText || spinDisplay ? `<span class="m-lb-specs">${specText}${specText && spinDisplay ? ' · ' : ''}${spinDisplay}</span>` : '';
      const offlineVal = parseFloat(e.formatted.offline) || 0;
      const accuracyLine = offlineVal > 0 ? `<span class="m-lb-accuracy">${e.formatted.offline} offline</span>` : '';
      div.innerHTML = `
        <span class="m-lb-rank">${i + 1}</span>
        <span class="m-lb-color" style="background:${e.colorHex}"></span>
        <span class="m-lb-info"><span class="m-lb-name">${e.label}</span>${specLine}</span>
        ${accuracyLine}
        <span class="m-lb-total">${e.formatted.total} total</span>
      `;

      // Tap to highlight this shot tracer + select config + show detail
      div.style.cursor = 'pointer';
      div.addEventListener('click', () => {
        container.querySelectorAll('.m-lb-entry').forEach(el => el.classList.remove('m-lb-selected'));
        div.classList.add('m-lb-selected');
        this.orchestrator.highlightShot(shotIndex, 0.10);
        this.showSelectedDetail(type, e);
        if (type === 'spec') {
          const results = this.orchestrator.getResults();
          const origIdx = shotIndex + 1;
          if (results[origIdx]) {
            this.onSelectConfig(results, origIdx);
            this.setSelectionCta('spec', e);
          }
        } else if (type === 'gear') {
          this.selectedGearEntry = e;
          this.displayedGearTotal = parseFloat(e.formatted.total) || 0;
          this.setSelectionCta('gear', e);
        } else if (type === 'ball') {
          this.selectedBallEntry = e;
          this.displayedBallTotal = parseFloat(e.formatted.total) || 0;
          this.setSelectionCta('ball', e);
        }
      });

      container.appendChild(div);
    }

    wrapper.style.display = '';
  }

  /** Update in-flight leaderboard with real-time carry distances. */
  private updateInFlightLeaderboard(data: { index: number; label: string; colorHex: string; carryYards: number; spinRate: number; specSummary: string; inFlight: boolean; offline: number }[]): void {
    // Determine which leaderboard to update based on phase
    const phase = this.orchestrator.phase;
    const type = phase === TestPhase.BallTesting ? 'ball' : phase === TestPhase.GearTesting ? 'gear' : 'spec';
    const container = document.getElementById(`m-${type}-lb-entries`);
    if (!container) return;

    // Sort: in-flight configs first (active tranche at top), then landed configs.
    // Within each group, sort by carry desc. This keeps the leaderboard visually
    // alive during each tranche — users see actively flying shots updating at top,
    // with completed configs from earlier tranches below.
    const sorted = [...data].sort((a, b) => {
      if (a.inFlight !== b.inFlight) return a.inFlight ? -1 : 1; // in-flight first
      return b.carryYards - a.carryYards;
    });
    // Show more entries for ball testing (up to 14 balls), 10 for specs/gear
    const maxEntries = type === 'ball' ? 14 : 10;
    const top = sorted.slice(0, maxEntries);

    // Update progress pill — use total spec count (not just launched-so-far)
    // so the pill reflects overall progress across all tranches
    const landedCount = data.filter(d => !d.inFlight).length;
    const totalCount = type === 'spec' ? this.orchestrator.getSpecCount()
                     : type === 'ball' ? this.orchestrator.getBallConfigMeta().length
                     : this.orchestrator.getGearConfigMeta().length;
    if (landedCount > 0) {
      this.updateProgressPill(landedCount, totalCount);
    }

    // Don't overwrite if a completed-shot leaderboard was already rendered
    const wrapper = document.getElementById(`m-${type}-leaderboard`);
    if (wrapper?.dataset.landed === 'true') return;

    container.innerHTML = '';
    for (let i = 0; i < top.length; i++) {
      const e = top[i];
      const div = document.createElement('div');
      div.className = `m-lb-entry rank-${i + 1}${e.inFlight ? ' in-flight' : ''}`;
      div.dataset.shotIndex = String(e.index);
      const spinDisplay = e.spinRate >= 1000 ? (e.spinRate / 1000).toFixed(1) + 'k spin' : e.spinRate > 0 ? Math.round(e.spinRate) + ' spin' : '';
      const specLine = e.specSummary ? `${e.specSummary}${spinDisplay ? ' · ' + spinDisplay : ''}` : spinDisplay;
      const accuracyLine = !e.inFlight && e.offline > 0 ? `<span class="m-lb-accuracy">${e.offline.toFixed(1)} offline</span>` : '';
      div.innerHTML = `
        <span class="m-lb-rank">${i + 1}</span>
        <span class="m-lb-color" style="background:${e.colorHex}"></span>
        <span class="m-lb-info"><span class="m-lb-name">${e.label}</span>${specLine ? `<span class="m-lb-specs">${specLine}</span>` : ''}</span>
        ${accuracyLine}
        <span class="m-lb-total">${e.carryYards.toFixed(1)} yd${e.inFlight ? ' · live' : ''}</span>
      `;

      // Tap to highlight this shot tracer
      div.style.cursor = 'pointer';
      div.addEventListener('click', () => {
        container.querySelectorAll('.m-lb-entry').forEach(el => el.classList.remove('m-lb-selected'));
        div.classList.add('m-lb-selected');
        this.orchestrator.highlightShot(e.index, 0.15);
      });

      container.appendChild(div);
    }
  }

  /** Build a single mobile result card element. */
  private buildMobileCard(
    entry: ComparisonEntry,
    rank: number,
    isBaseline: boolean,
    baseEntry?: ComparisonEntry,
  ): HTMLElement {
    const card = document.createElement('div');
    card.className = 'm-result-card';

    const carry = parseFloat(entry.formatted.carry) || 0;
    const total = parseFloat(entry.formatted.total) || 0;
    const spinDisplay = entry.spinRate >= 1000
      ? (entry.spinRate / 1000).toFixed(1) + 'k'
      : String(Math.round(entry.spinRate));

    let carryDelta = '';
    let totalDelta = '';
    if (!isBaseline && baseEntry) {
      const bc = parseFloat(baseEntry.formatted.carry) || 0;
      const bt = parseFloat(baseEntry.formatted.total) || 0;
      const dc = carry - bc;
      const dt = total - bt;
      if (dc !== 0) carryDelta = `<span class="m-card-delta ${dc > 0 ? 'pos' : 'neg'}">${dc > 0 ? '+' : ''}${dc.toFixed(1)}</span>`;
      if (dt !== 0) totalDelta = `<span class="m-card-delta ${dt > 0 ? 'pos' : 'neg'}">${dt > 0 ? '+' : ''}${dt.toFixed(1)}</span>`;
    }

    let rankBadge = '';
    if (rank === 1) rankBadge = '<span class="m-card-rank rank-1">#1</span>';
    else if (rank === 2) rankBadge = '<span class="m-card-rank rank-2">#2</span>';
    else if (rank === 3) rankBadge = '<span class="m-card-rank rank-3">#3</span>';

    // Favorites heart
    const isFav = this.favoritesManager.isFavorite(entry.label, entry.specSummary);
    const favClass = isFav ? ' favorited' : '';
    const favChar = isFav ? '\u2665' : '\u2661';
    const favHtml = isBaseline ? '' : `<button class="m-card-fav${favClass}">${favChar}</button>`;

    card.innerHTML = `
      <div class="m-card-color-strip" style="background:${entry.colorHex}"></div>
      <div class="m-card-body">
        <div class="m-card-header">${rankBadge}<span class="m-card-title">${isBaseline ? 'Baseline' : entry.label}</span></div>
        <div class="m-card-spec">${entry.specSummary}</div>
        <div class="m-card-stats">
          <div><div class="m-card-stat-label">Carry</div><span class="m-card-stat-primary">${entry.formatted.carry}<span class="m-card-stat-unit">yd</span></span>${carryDelta}</div>
          <div><div class="m-card-stat-label">Total</div><span class="m-card-stat-primary">${entry.formatted.total}<span class="m-card-stat-unit">yd</span></span>${totalDelta}</div>
        </div>
        <div class="m-card-secondary">
          <span>Ht ${entry.formatted.height}</span>
          <span>Land ${entry.formatted.landing}\u00B0</span>
          <span>Spin ${spinDisplay}</span>
          <span>Off ${entry.formatted.offline || '--'}</span>
        </div>
        ${favHtml}
      </div>
    `;

    // Wire favorite button
    if (!isBaseline) {
      const favBtn = card.querySelector('.m-card-fav') as HTMLElement | null;
      if (favBtn) {
        favBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const nowFav = this.favoritesManager.toggle({
            type: entry.specs ? 'spec' : 'gear',
            label: entry.label,
            specSummary: entry.specSummary,
            colorHex: entry.colorHex,
            formatted: entry.formatted,
            specs: entry.specs ? { ...entry.specs } : undefined,
          });
          favBtn.classList.toggle('favorited', nowFav);
          favBtn.textContent = nowFav ? '\u2665' : '\u2661';
        });
      }
    }

    return card;
  }

}
